import React, { useEffect, useMemo, useState } from "react";
import { api, ApiClientError } from "../api";
import { usePath, useNavigate, matchPath } from "../router";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { formatDateTime } from "../lib/format";
import { PremiumAnatomicalMapWorkspace } from "../components/clinical/PremiumAnatomicalMapWorkspace";
import type {
  ClinicalAlert,
  ClinicalEncounter,
  ClinicalNote,
  ClinicalRecord as ClinicalRecordType,
  ClinicalTimelineItem,
  Id,
  MedicalOrder,
  PatientConsent,
  PatientFile,
  Prescription,
  Professional,
  ClinicalProblem,
  Allergy,
  ActiveMedication,
  DiagnosticStudy,
  AnthropometricMeasurement,
  TreatmentProtocolTemplate,
  TreatmentProtocolRecord,
  ImplantDeviceRecord,
  PatientMedicalProfile,
  TreatmentRecordView,
  TreatmentRecordInsumoInput,
  TreatmentRecordEquipmentInput,
  ClinicalCareProgram,
  ClinicalCareProgramDetailResponse,
  Clinical360Report,
  Clinical360ReportSection,
} from "../types";

// Historia Clínica 360° — Fase 1 (Migración 12 + backend clínico ya
// cerrado). Deliberadamente NO es un formulario que refleja tablas: una
// sola pantalla con cabecera de paciente, alertas siempre visibles,
// timeline longitudinal y paneles con acciones contextuales, para que el
// profesional no tenga que navegar diez pantallas para atender a un
// paciente. Ruta: /pacientes/:id/historia.

const ENCOUNTER_TYPES = [
  { code: "CONSULTATION", label: "Consulta" },
  { code: "FOLLOW_UP", label: "Control" },
  { code: "PROCEDURE", label: "Procedimiento" },
  { code: "TELECONSULT", label: "Teleconsulta" },
  { code: "EMERGENCY", label: "Urgencia" },
];

const ORDER_TYPES = [
  { code: "LAB", label: "Laboratorio" },
  { code: "IMAGING", label: "Imágenes" },
  { code: "REFERRAL", label: "Derivación" },
  { code: "PROCEDURE_AUTH", label: "Autorización de procedimiento" },
];

const FILE_TYPES = [
  { code: "PHOTO", label: "Fotografía" },
  { code: "VIDEO", label: "Video" },
  { code: "LAB_RESULT", label: "Resultado de laboratorio" },
  { code: "IMAGING", label: "Estudio por imágenes" },
  { code: "DOCUMENT", label: "Documento" },
  { code: "CONSENT_SCAN", label: "Consentimiento escaneado" },
];

// Historia longitudinal — Fase 1B frontend (checkpoint posterior al
// backend, 2026-08-23). Catálogos que replican los sembrados por
// Migración 14 (diagnostic_study_types, clinical_alert_types) sólo para
// combos — la validación real sigue siendo 100% servidor (FK/CHECK).
const STUDY_TYPES = [
  { code: "LAB", label: "Laboratorio" },
  { code: "ULTRASOUND", label: "Ecografía" },
  { code: "CT", label: "Tomografía" },
  { code: "MRI", label: "Resonancia" },
  { code: "XRAY", label: "Radiografía" },
  { code: "ECG", label: "ECG" },
  { code: "DOPPLER", label: "Eco Doppler" },
  { code: "MAMMOGRAPHY", label: "Mamografía" },
  { code: "PATHOLOGY", label: "Anatomía patológica" },
  { code: "BODY_COMPOSITION", label: "Composición corporal" },
  { code: "ANTHROPOMETRY", label: "Antropometría" },
  { code: "PREOP", label: "Estudios prequirúrgicos" },
  { code: "OTHER", label: "Otro" },
];

const STUDY_STATUSES = ["ORDERED", "SCHEDULED", "RECEIVED", "REVIEWED", "ABNORMAL", "COMPLETED", "CANCELLED"];
const RESULT_FLAGS = ["NORMAL", "LOW", "HIGH", "CRITICAL", "TEXTUAL"];
const PROBLEM_STATUSES = ["ACTIVE", "RESOLVED", "INACTIVE", "RULED_OUT"];
const PREGNANCY_OPTIONS = ["N/A", "PRIMER_TRIMESTRE", "SEGUNDO_TRIMESTRE", "TERCER_TRIMESTRE", "PUERPERIO"];

// El paciente es el firmante del consentimiento — un usuario interno solo
// puede REGISTRAR que esa firma llegó, nunca "firmar" en su nombre. Por
// eso no hay una opción genérica acá: cada valor describe una forma real
// de evidencia. PATIENT_PORTAL_ESIGN queda reservado para cuando exista
// Portal del Paciente (todavía no construido) — no se ofrece en el combo.
const CONSENT_SIGNATURE_METHODS: { code: import("../types").ConsentSignatureMethod; label: string }[] = [
  { code: "IN_PERSON_PAPER", label: "Documento en papel firmado (escaneado/archivado)" },
  { code: "IN_PERSON_VERBAL_WITNESSED", label: "Consentimiento verbal presenciado" },
  { code: "RECEIVED_SIGNED_DOCUMENT", label: "Documento firmado recibido por otro medio" },
];

type TabKey = "actual" | "notas" | "recetas" | "ordenes" | "consentimientos" | "archivos" | "tratamiento" | "longitudinal" | "comparativas" | "informes";

const TABS: { key: TabKey; label: string }[] = [
  { key: "actual", label: "Consulta actual" },
  { key: "notas", label: "Notas" },
  { key: "recetas", label: "Recetas" },
  { key: "ordenes", label: "Órdenes" },
  { key: "consentimientos", label: "Consentimientos" },
  { key: "archivos", label: "Archivos" },
  { key: "tratamiento", label: "Tratamiento" },
  { key: "longitudinal", label: "Historia longitudinal" },
  { key: "comparativas", label: "Comparativas" },
  { key: "informes", label: "Informes" },
];

const TIMELINE_KIND_LABEL: Record<string, string> = {
  ENCOUNTER: "Encuentro clínico",
  NOTE_SIGNED: "Nota firmada",
  PRESCRIPTION_SIGNED: "Receta firmada",
  MEDICAL_ORDER_SIGNED: "Orden médica firmada",
  CONSENT_SIGNED: "Consentimiento firmado",
  FILE_ATTACHED: "Archivo adjuntado",
};

export default function ClinicalRecordPage() {
  const path = usePath();
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const params = matchPath("/pacientes/:id/historia", path);
  const contactId: Id | null = params?.id ?? null;

  const [record, setRecord] = useState<ClinicalRecordType | null>(null);
  const [timeline, setTimeline] = useState<ClinicalTimelineItem[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [orders, setOrders] = useState<MedicalOrder[]>([]);
  const [consents, setConsents] = useState<PatientConsent[]>([]);
  const [files, setFiles] = useState<PatientFile[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [alerts, setAlerts] = useState<ClinicalAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<TabKey>("actual");

  // Historia Clínica 360: el listado permite elegir el proceso
  // asistencial y el detalle consolida sesiones, archivos,
  // antropometría, comparaciones y mapas.
  const [carePrograms, setCarePrograms] =
    useState<ClinicalCareProgram[]>([]);
  const [
    selectedProgramId,
    setSelectedProgramId,
  ] = useState("");
  const [
    careProgramDetail,
    setCareProgramDetail,
  ] =
    useState<ClinicalCareProgramDetailResponse | null>(
      null
    );
  const [
    careProgramLoading,
    setCareProgramLoading,
  ] = useState(false);

  // Frontend longitudinal de Fase 1B (checkpoint posterior al backend,
  // 2026-08-23) — las 7 entidades de Migración 14.
  const [problems, setProblems] = useState<ClinicalProblem[]>([]);
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [medications, setMedications] = useState<ActiveMedication[]>([]);
  const [studies, setStudies] = useState<DiagnosticStudy[]>([]);
  const [measurements, setMeasurements] = useState<AnthropometricMeasurement[]>([]);
  const [protocolTemplates, setProtocolTemplates] = useState<TreatmentProtocolTemplate[]>([]);
  const [protocolRecords, setProtocolRecords] = useState<TreatmentProtocolRecord[]>([]);
  const [implants, setImplants] = useState<ImplantDeviceRecord[]>([]);

  // Heurística de conveniencia (no de seguridad — la firma la valida el
  // backend por identidad real): si el usuario logueado es `professional`,
  // preseleccionamos el profesional cuyo nombre coincide en los combos.
  const myLikelyProfessionalId = useMemo(() => {
    if (!user || user.role_code !== "professional") return "";
    const match = professionals.find((p) => p.full_name === user.full_name);
    return match ? String(match.id) : "";
  }, [user, professionals]);

  async function loadClinical360(
    preferredProgramId?: Id
  ) {
    if (!contactId) return;

    setCareProgramLoading(true);

    try {
      const response =
        await api.getClinicalCarePrograms(
          contactId
        );

      setCarePrograms(response.programs);

      const preferred =
        preferredProgramId == null
          ? selectedProgramId
          : String(preferredProgramId);

      const selected =
        response.programs.find(
          (program) =>
            String(program.id) ===
            preferred
        ) ||
        response.programs.find(
          (program) =>
            program.status === "ACTIVE"
        ) ||
        response.programs[0];

      if (!selected) {
        setSelectedProgramId("");
        setCareProgramDetail(null);
        return;
      }

      setSelectedProgramId(
        String(selected.id)
      );

      const detail =
        await api.getClinicalCareProgram(
          selected.id
        );

      setCareProgramDetail(detail);
    } catch (error) {
      setCarePrograms([]);
      setSelectedProgramId("");
      setCareProgramDetail(null);

      if (error instanceof ApiClientError) {
        show(error.message, "error");
      } else {
        show(
          "No se pudo cargar la Historia Clínica 360.",
          "error"
        );
      }
    } finally {
      setCareProgramLoading(false);
    }
  }

  async function reload() {
    if (!contactId) return;
    setLoading(true);
    try {
      const [rec, tl, rx, ord, cons, fl, al, probs, allg, meds, stud, meas, tpl, prot, impl] = await Promise.all([
        api.getClinicalRecord(contactId),
        api.getClinicalTimeline(contactId),
        api.getPatientPrescriptions(contactId),
        api.getPatientMedicalOrders(contactId),
        api.getPatientConsents(contactId),
        api.getPatientFiles(contactId),
        api.getClinicalAlerts(contactId),
        api.getClinicalProblems(contactId),
        api.getAllergies(contactId),
        api.getActiveMedications(contactId),
        api.getDiagnosticStudies(contactId),
        api.getAnthropometricMeasurements(contactId),
        api.getTreatmentProtocolTemplates(),
        api.getTreatmentProtocolRecords(contactId),
        api.getImplantDevices(contactId),
      ]);
      setRecord(rec);
      setTimeline(tl.timeline);
      setPrescriptions(rx);
      setOrders(ord);
      setConsents(cons);
      setFiles(fl);
      setAlerts(al);
      setProblems(probs);
      setAllergies(allg);
      setMedications(meds);
      setStudies(stud);
      setMeasurements(meas);
      setProtocolTemplates(tpl);
      setProtocolRecords(prot);
      setImplants(impl);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo cargar la historia clínica.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    loadClinical360();
    api.getProfessionals().then(setProfessionals).catch(() => {});
    setTab("actual");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  if (!contactId) {
    return <div className="chart-empty">Paciente no especificado.</div>;
  }

  if (loading && !record) {
    return <div className="loading-strip">Cargando historia clínica...</div>;
  }

  if (!record) {
    return <div className="chart-empty">No se pudo cargar la historia clínica de este paciente.</div>;
  }

  // medical_profile ya no alimenta AlertsBar (migrado a clinical_alerts,
  // ver más abajo) — la política de visibilidad legacy de
  // allergies/current_medications (mostrar el texto libre sólo mientras
  // no exista fila estructurada de esa categoría, fijada en la revisión
  // de Migración 14) queda deliberadamente fuera de este checkpoint, no
  // es deuda oculta: no hay todavía ninguna pantalla que liste
  // allergies/active_medications estructuradas para comparar contra.
  const { contact } = record;
  const openEncounter = record.encounters.find((e) => e.status === "OPEN") || null;

  return (
    <div className="chart-page">
      <button className="btn-ghost" onClick={() => navigate("/leads")} style={{ marginBottom: 10 }}>
        ← Volver
      </button>

      <PatientHeader
        contact={contact}
        openEncounter={openEncounter}
        professionals={professionals}
        defaultProfessionalId={myLikelyProfessionalId}
        onOpened={reload}
      />

      <AlertsBar alerts={alerts} contact={contact} onChanged={reload} />

      <div className="chart-body">
        <TimelineColumn timeline={timeline} />

        <div className="chart-main-col">
          <div
            className="chart-encounter-card"
            style={{ marginBottom: 12 }}
          >
            <div className="chart-list-actions">
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 15,
                  }}
                >
                  Proceso asistencial 360°
                </h3>
                <div className="meta">
                  Contexto longitudinal para sesiones,
                  evolución, fotografías y mapas.
                </div>
              </div>

              {careProgramLoading && (
                <span className="meta">
                  Actualizando…
                </span>
              )}
            </div>

            {carePrograms.length === 0 ? (
              <div
                className="chart-empty"
                style={{ marginTop: 8 }}
              >
                Este paciente todavía no tiene un
                programa asistencial disponible.
              </div>
            ) : (
              <>
                <select
                  value={selectedProgramId}
                  disabled={careProgramLoading}
                  onChange={(event: any) => {
                    const nextId =
                      event.target.value;

                    setSelectedProgramId(nextId);
                    loadClinical360(nextId);
                  }}
                  style={{
                    width: "100%",
                    marginTop: 10,
                  }}
                >
                  {carePrograms.map(
                    (program) => (
                      <option
                        key={program.id}
                        value={String(program.id)}
                      >
                        {program.title} · {
                          program.status
                        } · {
                          program
                            .responsible_professional_name ||
                          "Profesional"
                        }
                      </option>
                    )
                  )}
                </select>

                {careProgramDetail && (
                  <div
                    className="meta"
                    style={{ marginTop: 8 }}
                  >
                    {
                      careProgramDetail.program
                        .program_type
                    }
                    {" · "}
                    {
                      careProgramDetail.sessions
                        .length
                    } sesiones
                    {" · "}
                    {
                      careProgramDetail.files
                        .length
                    } archivos
                    {" · "}
                    {
                      careProgramDetail
                        .comparisons.length
                    } comparaciones
                  </div>
                )}
              </>
            )}
          </div>

          <div className="chart-tabs">
            {TABS.map((t) => (
              <button key={t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="chart-panel">
            {tab === "actual" && (
              <CurrentEncounterPanel
                encounter={openEncounter}
                professionals={professionals}
                defaultProfessionalId={myLikelyProfessionalId}
                onChanged={reload}
              />
            )}
            {tab === "notas" && <NotesPanel encounters={record.encounters} />}
            {tab === "recetas" && (
              <PrescriptionsPanel
                contactId={contactId}
                encounter={openEncounter}
                prescriptions={prescriptions}
                professionals={professionals}
                defaultProfessionalId={myLikelyProfessionalId}
                onChanged={reload}
              />
            )}
            {tab === "ordenes" && (
              <OrdersPanel
                contactId={contactId}
                encounter={openEncounter}
                orders={orders}
                professionals={professionals}
                defaultProfessionalId={myLikelyProfessionalId}
                onChanged={reload}
              />
            )}
            {tab === "consentimientos" && (
              <ConsentsPanel
                contactId={contactId}
                encounter={openEncounter}
                consents={consents}
                professionals={professionals}
                defaultProfessionalId={myLikelyProfessionalId}
                onChanged={reload}
              />
            )}
            {tab === "archivos" && (
              <FilesPanel
                contactId={contactId}
                encounter={openEncounter}
                files={files}
                professionalId={
                  myLikelyProfessionalId
                    ? Number(
                        myLikelyProfessionalId
                      )
                    : null
                }
                programId={
                  selectedProgramId ||
                  undefined
                }
                sessions={
                  careProgramDetail
                    ?.sessions || []
                }
                onChanged={async () => {
                  await reload();
                  await loadClinical360(
                    selectedProgramId ||
                    undefined
                  );
                }}
              />
            )}
            {tab === "tratamiento" && <TreatmentPanel encounter={openEncounter} />}
            {tab === "longitudinal" && (
              <LongitudinalPanel
                contactId={contactId}
                medicalProfile={record.medical_profile}
                professionals={professionals}
                defaultProfessionalId={myLikelyProfessionalId}
                myProfessionalId={myLikelyProfessionalId ? Number(myLikelyProfessionalId) : null}
                problems={problems}
                allergies={allergies}
                medications={medications}
                studies={studies}
                measurements={measurements}
                programId={
                  selectedProgramId ||
                  undefined
                }
                sessions={
                  careProgramDetail
                    ?.sessions || []
                }
                protocolTemplates={protocolTemplates}
                protocolRecords={protocolRecords}
                implants={implants}
                onChanged={async () => {
                  await reload();
                  await loadClinical360(
                    selectedProgramId ||
                    undefined
                  );
                }}
              />
            )}
            {tab === "informes" && (
              <ClinicalReportsPanel
                detail={careProgramDetail}
                loading={careProgramLoading}
                defaultProfessionalId={
                  myLikelyProfessionalId
                }
                onChanged={() =>
                  loadClinical360(
                    selectedProgramId ||
                    undefined
                  )
                }
              />
            )}
            {tab === "comparativas" && (
              <ComparativesPanel
                record={record}
                prescriptions={prescriptions}
                orders={orders}
                consents={consents}
                files={files}
                measurements={measurements}
                programDetail={
                  careProgramDetail
                }
                programLoading={
                  careProgramLoading
                }
                onProgramChanged={() =>
                  loadClinical360(
                    selectedProgramId ||
                    undefined
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Cabecera de paciente
// ---------------------------------------------------------------------
function PatientHeader({
  contact,
  openEncounter,
  professionals,
  defaultProfessionalId,
  onOpened,
}: {
  contact: any;
  openEncounter: ClinicalEncounter | null;
  professionals: Professional[];
  defaultProfessionalId: string;
  onOpened: () => void;
}) {
  const { show } = useToast();
  const [opening, setOpening] = useState(false);
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);
  const [encounterType, setEncounterType] = useState("CONSULTATION");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => setProfessionalId(defaultProfessionalId), [defaultProfessionalId]);

  async function openNewEncounter() {
    if (!professionalId) {
      show("Elegí un profesional para abrir el encuentro.", "error");
      return;
    }
    setSubmitting(true);
    try {
      await api.openEncounter({
        contact_id: contact.id,
        professional_id: professionalId,
        encounter_type: encounterType,
        chief_complaint: chiefComplaint || undefined,
      });
      show("Encuentro clínico abierto.", "success");
      setChiefComplaint("");
      setOpening(false);
      onOpened();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo abrir el encuentro.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="chart-header">
      <div className="chart-header-id">
        <h1>{contact.full_name}</h1>
        <div className="chart-meta">
          {contact.patient_number ? `Paciente Nº ${contact.patient_number} · ` : ""}
          {contact.phone} {contact.email ? `· ${contact.email}` : ""}
        </div>
      </div>
      <div className="chart-header-actions">
        {openEncounter ? (
          <span className="pill pill-ok">Consulta en curso — {formatDateTime(openEncounter.started_at)}</span>
        ) : opening ? (
          <div className="chart-form" style={{ minWidth: 260 }}>
            <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
              <option value="">Profesional...</option>
              {professionals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                </option>
              ))}
            </select>
            <select value={encounterType} onChange={(e: any) => setEncounterType(e.target.value)}>
              {ENCOUNTER_TYPES.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              placeholder="Motivo de consulta (opcional)"
              value={chiefComplaint}
              onChange={(e: any) => setChiefComplaint(e.target.value)}
            />
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={openNewEncounter}>
                Abrir
              </button>
              <button className="btn-ghost" onClick={() => setOpening(false)}>
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button className="btn-primary" onClick={() => setOpening(true)}>
            + Abrir consulta
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Barra de alertas — siempre visible, independiente de la pestaña activa.
//
// Fase 1B backend (checkpoint posterior a Migración 14, 2026-08-23):
// las alertas CLÍNICAS ya no se calculan acá leyendo medical_profile —
// vienen de `clinical_alerts` (GET /clinical/clinical-alerts), la única
// fuente de verdad (ver FASE1B_RECONCILIACION.md §3). Antes de este
// cambio, AlertsBar calculaba alergia/embarazo/medicación ad hoc mirando
// patient_medical_profiles directamente; eso ya no existe acá.
// ---------------------------------------------------------------------
function AlertsBar({
  alerts,
  contact,
  onChanged,
}: {
  alerts: ClinicalAlert[];
  contact: any;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const { show } = useToast();
  const [busyId, setBusyId] = useState<Id | null>(null);
  // Mismo gate que el backend (CLINICAL_WRITE_ROLES): admin/director/
  // professional pueden reconocer/resolver; reception ni siquiera llega
  // acá (no puede leer clinical-alerts, ver clinicalLongitudinal.js).
  const canManage = !!user && ["admin", "director", "professional"].includes(user.role_code);

  async function acknowledge(id: Id) {
    setBusyId(id);
    try {
      await api.acknowledgeClinicalAlert(id);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo reconocer la alerta.", "error");
    } finally {
      setBusyId(null);
    }
  }

  async function resolve(id: Id) {
    setBusyId(id);
    try {
      await api.resolveClinicalAlert(id);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo resolver la alerta.", "error");
    } finally {
      setBusyId(null);
    }
  }

  // Avisos ADMINISTRATIVOS (consentimiento de comunicación) — jerarquía
  // menor a propósito: no deben competir visualmente con una alerta
  // clínica, aunque sigan siempre visibles. Sigue siendo un aviso
  // derivado de contacts.communication_consent, NO una clinical_alert —
  // conceptualmente distinto (ver FASE1B_RECONCILIACION.md §3, punto 2).
  const adminNotices: string[] = [];
  if (contact.communication_consent === null || contact.communication_consent === undefined) {
    adminNotices.push("Sin evidencia de consentimiento de comunicación registrada.");
  } else if (contact.communication_consent === false) {
    adminNotices.push("El paciente declinó el consentimiento de comunicación.");
  }

  if (alerts.length === 0 && adminNotices.length === 0) return null;

  const severityClass = (s: ClinicalAlert["severity"]) => (s === "SEVERE" ? "severe" : s === "INFO" ? "info" : "");

  return (
    <div className="chart-alerts">
      {alerts.map((a) => (
        <div key={a.id} className={`chart-alert ${severityClass(a.severity)}`}>
          <span>
            ⚠ {a.title}
            {a.status === "ACKNOWLEDGED" && <em style={{ marginLeft: 6, opacity: 0.75 }}>(reconocida)</em>}
          </span>
          {canManage && (
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {a.status === "OPEN" && (
                <button className="btn-ghost" disabled={busyId === a.id} onClick={() => acknowledge(a.id)}>
                  Reconocer
                </button>
              )}
              <button className="btn-ghost" disabled={busyId === a.id} onClick={() => resolve(a.id)}>
                Resolver
              </button>
            </span>
          )}
        </div>
      ))}
      {adminNotices.map((text, i) => (
        <div key={`admin-${i}`} className="chart-notice">
          {text}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Timeline longitudinal
// ---------------------------------------------------------------------
function TimelineColumn({ timeline }: { timeline: ClinicalTimelineItem[] }) {
  return (
    <div className="chart-timeline-col">
      <h3>Timeline longitudinal</h3>
      {timeline.length === 0 && <p className="chart-empty">Todavía no hay hechos clínicos firmados.</p>}
      <div className="chart-timeline-rail">
        {timeline.map((ev) => (
          <div className="chart-timeline-item" key={`${ev.kind}-${ev.id}`}>
            <div className="kind">{TIMELINE_KIND_LABEL[ev.kind] || ev.kind}</div>
            <div className="when">{formatDateTime(ev.occurred_at)}</div>
            {ev.detail && Object.keys(ev.detail).length > 0 && (
              <div className="detail">
                {Object.entries(ev.detail)
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Consulta actual — panel de acción principal: notas del encuentro
// abierto, firmar / enmendar sin salir de la pantalla.
// ---------------------------------------------------------------------
function CurrentEncounterPanel({
  encounter,
  professionals,
  defaultProfessionalId,
  onChanged,
}: {
  encounter: ClinicalEncounter | null;
  professionals: Professional[];
  defaultProfessionalId: string;
  onChanged: () => void;
}) {
  if (!encounter) {
    return <div className="chart-empty">No hay una consulta en curso. Usá "Abrir consulta" arriba para empezar una.</div>;
  }

  return (
    <div className="chart-encounter-card">
      <div className="head">
        <div>
          <b>{ENCOUNTER_TYPES.find((t) => t.code === encounter.encounter_type)?.label || encounter.encounter_type}</b>{" "}
          <span className="meta">· iniciada {formatDateTime(encounter.started_at)}</span>
        </div>
        <span className="pill pill-ok">{encounter.status}</span>
      </div>
      {encounter.chief_complaint && <p className="meta">Motivo: {encounter.chief_complaint}</p>}

      <h3 style={{ fontSize: 14, marginTop: 14 }}>Notas de esta consulta</h3>
      {(encounter.notes || []).map((n) => (
        <NoteCard key={n.id} note={n} professionals={professionals} defaultProfessionalId={defaultProfessionalId} onChanged={onChanged} />
      ))}

      <NewNoteForm encounterId={encounter.id} professionals={professionals} defaultProfessionalId={defaultProfessionalId} onCreated={onChanged} />
    </div>
  );
}

// Botón de firma consciente de `can_sign` (ajuste #2 del checkpoint de
// revisión): el backend sigue siendo la única autoridad real — un 403 del
// servidor sigue pasando si algo cambia entre el render y el click — pero
// evitamos la experiencia frustrante de mostrar "Firmar" a alguien que
// nunca va a poder. `can_sign === undefined` (respuesta vieja, sin este
// campo) se trata como "no lo sabemos": mostramos el botón habilitado y
// dejamos que el 403 real del servidor decida, igual que antes de este
// ajuste.
function SignButton({ canSign, signing, onSign }: { canSign?: boolean; signing: boolean; onSign: () => void }) {
  if (canSign === false) {
    return (
      <span className="pill pill-bad" title="Solo puede firmar el profesional responsable de este registro.">
        Solo puede firmar el profesional responsable
      </span>
    );
  }
  return (
    <button className="btn-primary" disabled={signing} onClick={onSign}>
      Firmar
    </button>
  );
}

function NoteCard({
  note,
  professionals,
  defaultProfessionalId,
  onChanged,
}: {
  key?: Id;
  note: ClinicalNote;
  professionals: Professional[];
  defaultProfessionalId: string;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [signing, setSigning] = useState(false);
  const [amending, setAmending] = useState(false);
  const [amendText, setAmendText] = useState(note.plan_text || note.free_text || "");
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);

  async function sign() {
    setSigning(true);
    try {
      await api.signClinicalNote(note.id);
      show("Nota firmada.", "success");
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo firmar la nota.", "error");
    } finally {
      setSigning(false);
    }
  }

  async function submitAmend() {
    if (!professionalId) {
      show("Elegí el profesional que enmienda.", "error");
      return;
    }
    try {
      await api.amendClinicalNote(note.id, { professional_id: professionalId, plan_text: amendText });
      show("Enmienda registrada como nueva versión.", "success");
      setAmending(false);
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo enmendar la nota.", "error");
    }
  }

  return (
    <div className={`chart-note ${note.status === "SIGNED" ? "signed" : ""}`}>
      <div className="note-head">
        <span>
          v{note.version} · <span className={note.status === "SIGNED" ? "pill pill-ok" : "pill pill-warn"}>{note.status}</span>
        </span>
        <span>{formatDateTime(note.signed_at || note.created_at)}</span>
      </div>
      {note.subjective_text && <p><b>S:</b> {note.subjective_text}</p>}
      {note.objective_text && <p><b>O:</b> {note.objective_text}</p>}
      {note.assessment_text && <p><b>A:</b> {note.assessment_text}</p>}
      {note.plan_text && <p><b>P:</b> {note.plan_text}</p>}
      {note.free_text && <p>{note.free_text}</p>}

      <div className="row">
        {note.status === "DRAFT" && (
          <SignButton canSign={note.can_sign} signing={signing} onSign={sign} />
        )}
        {note.status === "SIGNED" && !amending && (
          <button className="btn-ghost" onClick={() => setAmending(true)}>
            Enmendar
          </button>
        )}
      </div>

      {amending && (
        <div className="chart-form">
          <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
            <option value="">Profesional que enmienda...</option>
            {professionals.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
              </option>
            ))}
          </select>
          <textarea rows={3} value={amendText} onChange={(e: any) => setAmendText(e.target.value)} placeholder="Texto de la enmienda (plan)" />
          <div className="row">
            <button className="btn-primary" onClick={submitAmend}>
              Guardar enmienda
            </button>
            <button className="btn-ghost" onClick={() => setAmending(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NewNoteForm({
  encounterId,
  professionals,
  defaultProfessionalId,
  onCreated,
}: {
  encounterId: Id;
  professionals: Professional[];
  defaultProfessionalId: string;
  onCreated: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => setProfessionalId(defaultProfessionalId), [defaultProfessionalId]);

  async function submit() {
    if (!professionalId) {
      show("Elegí el profesional autor de la nota.", "error");
      return;
    }
    setSubmitting(true);
    try {
      await api.createClinicalNote(encounterId, { professional_id: professionalId, free_text: text });
      show("Nota creada en borrador.", "success");
      setText("");
      setOpen(false);
      onCreated();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo crear la nota.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <button className="btn-ghost" style={{ marginTop: 10 }} onClick={() => setOpen(true)}>
        + Nueva nota
      </button>
    );
  }

  return (
    <div className="chart-form" style={{ marginTop: 10 }}>
      <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
        <option value="">Profesional...</option>
        {professionals.map((p) => (
          <option key={p.id} value={p.id}>
            {p.full_name}
          </option>
        ))}
      </select>
      <textarea rows={4} placeholder="Texto de la nota..." value={text} onChange={(e: any) => setText(e.target.value)} />
      <div className="row">
        <button className="btn-primary" disabled={submitting} onClick={submit}>
          Guardar borrador
        </button>
        <button className="btn-ghost" onClick={() => setOpen(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Notas — historial completo, agrupado por encuentro
// ---------------------------------------------------------------------
function NotesPanel({ encounters }: { encounters: ClinicalEncounter[] }) {
  const withNotes = encounters.filter((e) => (e.notes || []).length > 0);
  if (withNotes.length === 0) return <div className="chart-empty">Todavía no hay notas clínicas registradas.</div>;
  return (
    <div>
      {withNotes.map((e) => (
        <div className="chart-encounter-card" key={e.id}>
          <div className="head">
            <b>{ENCOUNTER_TYPES.find((t) => t.code === e.encounter_type)?.label || e.encounter_type}</b>
            <span className="meta">{formatDateTime(e.started_at)}</span>
          </div>
          {(e.notes || []).map((n) => (
            <div className={`chart-note ${n.status === "SIGNED" ? "signed" : ""}`} key={n.id}>
              <div className="note-head">
                <span>
                  v{n.version} · <span className={n.status === "SIGNED" ? "pill pill-ok" : "pill pill-warn"}>{n.status}</span>
                </span>
                <span>{formatDateTime(n.signed_at || n.created_at)}</span>
              </div>
              {n.plan_text && <p>{n.plan_text}</p>}
              {n.free_text && <p>{n.free_text}</p>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Recetas
// ---------------------------------------------------------------------
function PrescriptionsPanel({
  contactId,
  encounter,
  prescriptions,
  professionals,
  defaultProfessionalId,
  onChanged,
}: {
  contactId: Id;
  encounter: ClinicalEncounter | null;
  prescriptions: Prescription[];
  professionals: Professional[];
  defaultProfessionalId: string;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);
  const [instructions, setInstructions] = useState("");
  const [items, setItems] = useState([{ medication_name: "", dose: "", frequency: "", duration: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [signingId, setSigningId] = useState<Id | null>(null);

  useEffect(() => setProfessionalId(defaultProfessionalId), [defaultProfessionalId]);

  function updateItem(i: number, field: string, value: string) {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
  }

  async function submit() {
    if (!professionalId) return show("Elegí el profesional.", "error");
    const validItems = items.filter((it) => it.medication_name.trim());
    if (validItems.length === 0) return show("Agregá al menos un medicamento.", "error");
    setSubmitting(true);
    try {
      await api.createPrescription({
        contact_id: contactId,
        encounter_id: encounter?.id,
        professional_id: professionalId,
        instructions: instructions || undefined,
        items: validItems,
      });
      show("Receta creada en borrador.", "success");
      setItems([{ medication_name: "", dose: "", frequency: "", duration: "" }]);
      setInstructions("");
      setOpen(false);
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo crear la receta.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function sign(id: Id) {
    setSigningId(id);
    try {
      await api.signPrescription(id);
      show("Receta firmada.", "success");
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo firmar la receta.", "error");
    } finally {
      setSigningId(null);
    }
  }

  return (
    <div>
      <div className="chart-list-actions">
        <h3 style={{ margin: 0, fontSize: 14 }}>Recetas</h3>
        {!open && (
          <button className="btn-ghost" onClick={() => setOpen(true)}>
            + Nueva receta
          </button>
        )}
      </div>

      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
              <option value="">Profesional...</option>
              {professionals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                </option>
              ))}
            </select>
            {items.map((it, i) => (
              <div className="chart-item-row" key={i}>
                <input placeholder="Medicamento" value={it.medication_name} onChange={(e: any) => updateItem(i, "medication_name", e.target.value)} />
                <input placeholder="Dosis" value={it.dose} onChange={(e: any) => updateItem(i, "dose", e.target.value)} />
                <input placeholder="Frecuencia" value={it.frequency} onChange={(e: any) => updateItem(i, "frequency", e.target.value)} />
                <input placeholder="Duración" value={it.duration} onChange={(e: any) => updateItem(i, "duration", e.target.value)} />
                <button className="btn-ghost" onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))} disabled={items.length === 1}>
                  ✕
                </button>
              </div>
            ))}
            <button className="btn-ghost" onClick={() => setItems((prev) => [...prev, { medication_name: "", dose: "", frequency: "", duration: "" }])}>
              + Agregar medicamento
            </button>
            <textarea rows={2} placeholder="Indicaciones generales (opcional)" value={instructions} onChange={(e: any) => setInstructions(e.target.value)} />
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Guardar receta
              </button>
              <button className="btn-ghost" onClick={() => setOpen(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {prescriptions.length === 0 && !open && <div className="chart-empty">Sin recetas registradas.</div>}
      {prescriptions.map((p) => (
        <div className="chart-encounter-card" key={p.id}>
          <div className="head">
            <span className={p.status === "SIGNED" ? "pill pill-ok" : p.status === "CANCELLED" ? "pill pill-bad" : "pill pill-warn"}>{p.status}</span>
            <span className="meta">{formatDateTime(p.signed_at || p.created_at)}</span>
          </div>
          <ul>
            {p.items.map((it) => (
              <li key={it.id}>
                {it.medication_name} {it.dose ? `· ${it.dose}` : ""} {it.frequency ? `· ${it.frequency}` : ""} {it.duration ? `· ${it.duration}` : ""}
              </li>
            ))}
          </ul>
          {p.instructions && <p className="meta">{p.instructions}</p>}
          {p.status === "DRAFT" && (
            <SignButton canSign={p.can_sign} signing={signingId === p.id} onSign={() => sign(p.id)} />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Órdenes médicas
// ---------------------------------------------------------------------
function OrdersPanel({
  contactId,
  encounter,
  orders,
  professionals,
  defaultProfessionalId,
  onChanged,
}: {
  contactId: Id;
  encounter: ClinicalEncounter | null;
  orders: MedicalOrder[];
  professionals: Professional[];
  defaultProfessionalId: string;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);
  const [orderType, setOrderType] = useState("LAB");
  const [priority, setPriority] = useState("ROUTINE");
  const [indication, setIndication] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signingId, setSigningId] = useState<Id | null>(null);

  useEffect(() => setProfessionalId(defaultProfessionalId), [defaultProfessionalId]);

  async function submit() {
    if (!professionalId) return show("Elegí el profesional.", "error");
    if (!indication.trim()) return show("La indicación es requerida.", "error");
    setSubmitting(true);
    try {
      await api.createMedicalOrder({
        contact_id: contactId,
        encounter_id: encounter?.id,
        professional_id: professionalId,
        order_type: orderType,
        indication,
        priority,
      });
      show("Orden médica creada en borrador.", "success");
      setIndication("");
      setOpen(false);
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo crear la orden.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function sign(id: Id) {
    setSigningId(id);
    try {
      await api.signMedicalOrder(id);
      show("Orden firmada.", "success");
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo firmar la orden.", "error");
    } finally {
      setSigningId(null);
    }
  }

  return (
    <div>
      <div className="chart-list-actions">
        <h3 style={{ margin: 0, fontSize: 14 }}>Órdenes médicas</h3>
        {!open && (
          <button className="btn-ghost" onClick={() => setOpen(true)}>
            + Nueva orden
          </button>
        )}
      </div>

      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <div className="chart-form-row">
              <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
                <option value="">Profesional...</option>
                {professionals.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name}
                  </option>
                ))}
              </select>
              <select value={orderType} onChange={(e: any) => setOrderType(e.target.value)}>
                {ORDER_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <select value={priority} onChange={(e: any) => setPriority(e.target.value)}>
              <option value="ROUTINE">Rutina</option>
              <option value="URGENT">Urgente</option>
              <option value="STAT">Inmediata (STAT)</option>
            </select>
            <textarea rows={2} placeholder="Indicación" value={indication} onChange={(e: any) => setIndication(e.target.value)} />
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Guardar orden
              </button>
              <button className="btn-ghost" onClick={() => setOpen(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {orders.length === 0 && !open && <div className="chart-empty">Sin órdenes médicas registradas.</div>}
      {orders.map((o) => (
        <div className="chart-encounter-card" key={o.id}>
          <div className="head">
            <div>
              <b>{ORDER_TYPES.find((t) => t.code === o.order_type)?.label || o.order_type}</b>{" "}
              <span className={o.priority === "STAT" ? "pill pill-bad" : o.priority === "URGENT" ? "pill pill-warn" : "pill"}>{o.priority}</span>
            </div>
            <span className={o.status === "SIGNED" ? "pill pill-ok" : "pill pill-warn"}>{o.status}</span>
          </div>
          <p>{o.indication}</p>
          <p className="meta">{formatDateTime(o.signed_at || o.created_at)}</p>
          {o.status === "DRAFT" && (
            <SignButton canSign={o.can_sign} signing={signingId === o.id} onSign={() => sign(o.id)} />
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// Consentimientos
// ---------------------------------------------------------------------
function ConsentsPanel({
  contactId,
  encounter,
  consents,
  professionals,
  defaultProfessionalId,
  onChanged,
}: {
  contactId: Id;
  encounter: ClinicalEncounter | null;
  consents: PatientConsent[];
  professionals: Professional[];
  defaultProfessionalId: string;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [templateCode, setTemplateCode] = useState("");
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);
  const [submitting, setSubmitting] = useState(false);
  const [recordingId, setRecordingId] = useState<Id | null>(null);

  useEffect(() => setProfessionalId(defaultProfessionalId), [defaultProfessionalId]);

  async function submit() {
    if (!templateCode.trim()) return show("Indicá el código de la plantilla de consentimiento.", "error");
    setSubmitting(true);
    try {
      await api.createConsent({
        contact_id: contactId,
        template_code: templateCode.trim(),
        encounter_id: encounter?.id,
        professional_id: professionalId || undefined,
      });
      show("Consentimiento generado desde la plantilla.", "success");
      setTemplateCode("");
      setOpen(false);
      onChanged();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo generar el consentimiento.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <div className="chart-list-actions">
        <h3 style={{ margin: 0, fontSize: 14 }}>Consentimientos</h3>
        {!open && (
          <button className="btn-ghost" onClick={() => setOpen(true)}>
            + Solicitar consentimiento
          </button>
        )}
      </div>

      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <input placeholder="Código de plantilla (ej. CONSENT_PROCEDIMIENTO)" value={templateCode} onChange={(e: any) => setTemplateCode(e.target.value)} />
            <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
              <option value="">Profesional (opcional)...</option>
              {professionals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                </option>
              ))}
            </select>
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Generar
              </button>
              <button className="btn-ghost" onClick={() => setOpen(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {consents.length === 0 && !open && <div className="chart-empty">Sin consentimientos registrados.</div>}
      {consents.map((c) => (
        <div className="chart-encounter-card" key={c.id}>
          <div className="head">
            <b>{c.template?.name || c.template?.code || `Plantilla #${c.template_id}`}</b>
            <span className={c.status === "SIGNED" ? "pill pill-ok" : c.status === "REVOKED" ? "pill pill-bad" : "pill pill-warn"}>{c.status}</span>
          </div>
          <p className="meta">{formatDateTime(c.signed_at || c.sent_at)}</p>
          {c.status === "SIGNED" && c.evidence?.note && (
            <p className="meta">Evidencia: {c.evidence.note}</p>
          )}
          {(c.status === "REQUIRED" || c.status === "SENT" || c.status === "VIEWED") && (
            <RecordConsentSignatureForm
              consentId={c.id}
              recording={recordingId === c.id}
              onStart={() => setRecordingId(c.id)}
              onCancel={() => setRecordingId(null)}
              onRecorded={() => {
                setRecordingId(null);
                onChanged();
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// El paciente es el firmante del consentimiento — este formulario existe
// deliberadamente separado de un botón "Firmar" genérico. Un usuario
// interno REGISTRA que recibió esa firma (papel, verbal presenciada,
// documento por otro medio), nunca firma en nombre del paciente. El
// backend exige signature_method + evidence_note; acá se piden los mismos
// dos campos porque no tiene sentido dejar que el usuario los omita y
// recién entonces mostrarle un 400.
function RecordConsentSignatureForm({
  consentId,
  recording,
  onStart,
  onCancel,
  onRecorded,
}: {
  key?: Id;
  consentId: Id;
  recording: boolean;
  onStart: () => void;
  onCancel: () => void;
  onRecorded: () => void;
}) {
  const { show } = useToast();
  const [signatureMethod, setSignatureMethod] = useState<import("../types").ConsentSignatureMethod | "">("");
  const [evidenceNote, setEvidenceNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!signatureMethod) return show("Elegí cómo se recibió la firma.", "error");
    if (!evidenceNote.trim()) return show("Describí brevemente la evidencia (ej. dónde quedó archivado el documento).", "error");
    setSubmitting(true);
    try {
      await api.recordConsentSignature(consentId, { signature_method: signatureMethod, evidence_note: evidenceNote.trim() });
      show("Firma recibida registrada.", "success");
      onRecorded();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar la firma recibida.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (!recording) {
    return (
      <button className="btn-primary" onClick={onStart}>
        Registrar firma recibida
      </button>
    );
  }

  return (
    <div className="chart-form">
      <p className="meta">El paciente es quien firma. Esto registra que su firma fue recibida, no la reemplaza.</p>
      <select value={signatureMethod} onChange={(e: any) => setSignatureMethod(e.target.value)}>
        <option value="">¿Cómo se recibió la firma?...</option>
        {CONSENT_SIGNATURE_METHODS.map((m) => (
          <option key={m.code} value={m.code}>
            {m.label}
          </option>
        ))}
      </select>
      <textarea rows={2} placeholder="Evidencia (ej. documento archivado en carpeta X, testigo presente)" value={evidenceNote} onChange={(e: any) => setEvidenceNote(e.target.value)} />
      <div className="row">
        <button className="btn-primary" disabled={submitting} onClick={submit}>
          Registrar
        </button>
        <button className="btn-ghost" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Archivos
// ---------------------------------------------------------------------
function FilesPanel({
  contactId,
  encounter,
  files,
  professionalId,
  programId,
  sessions,
  onChanged,
}: {
  contactId: Id;
  encounter: ClinicalEncounter | null;
  files: PatientFile[];
  professionalId: number | null;
  programId?: Id;
  sessions: import("../types")
    .ClinicalCareSession[];
  onChanged: () => void | Promise<void>;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [fileType, setFileType] = useState("PHOTO");
  const [label, setLabel] = useState("");
  const [selectedFile, setSelectedFile] =
    useState<File | null>(null);
  const [sessionId, setSessionId] =
    useState("");
  const [capturePhase, setCapturePhase] =
    useState<
      "BEFORE" |
      "DURING" |
      "AFTER" |
      "FOLLOW_UP"
    >("FOLLOW_UP");
  const [
    comparisonReady,
    setComparisonReady,
  ] = useState(false);
  const [
    patientVisibility,
    setPatientVisibility,
  ] = useState<
    "INTERNAL" |
    "SHAREABLE" |
    "PUBLISHED"
  >("INTERNAL");
  const [
    downloadingId,
    setDownloadingId,
  ] = useState<Id | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!selectedFile) {
      show(
        "Seleccioná el archivo que querés subir.",
        "error"
      );
      return;
    }

    const allowedMimeTypes = new Set([
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/heic",
      "image/heif",
    ]);

    if (
      !allowedMimeTypes.has(
        selectedFile.type
      )
    ) {
      show(
        "Solo se permiten PDF, JPG, PNG, WEBP, HEIC o HEIF.",
        "error"
      );
      return;
    }

    if (
      selectedFile.size >
      15 * 1024 * 1024
    ) {
      show(
        "El archivo no puede superar 15 MB.",
        "error"
      );
      return;
    }

    setSubmitting(true);

    try {
      const initialized =
        await api.initializePrivateClinicalFile(
          contactId,
          {
            encounter_id:
              encounter?.id,
            professional_id:
              professionalId ||
              undefined,
            program_id:
              programId,
            session_id:
              sessionId ||
              undefined,
            file_type: fileType,
            original_filename:
              selectedFile.name,
            mime_type:
              selectedFile.type,
            size_bytes:
              selectedFile.size,
            label:
              label || undefined,
            captured_at:
              new Date().toISOString(),
            capture_phase:
              programId
                ? capturePhase
                : undefined,
            comparison_ready:
              programId
                ? comparisonReady
                : false,
            patient_visibility:
              patientVisibility,
          }
        );

      await api
        .uploadPrivateClinicalFileContent(
          contactId,
          initialized.file.id,
          selectedFile
        );

      show(
        "Archivo clínico cargado de forma privada.",
        "success"
      );

      setLabel("");
      setSelectedFile(null);
      setSessionId("");
      setCapturePhase("FOLLOW_UP");
      setComparisonReady(false);
      setPatientVisibility("INTERNAL");
      setOpen(false);

      await onChanged();
    } catch (error) {
      if (
        error instanceof ApiClientError
      ) {
        show(error.message, "error");
      } else {
        show(
          "No se pudo cargar el archivo clínico.",
          "error"
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function download(
    file: PatientFile
  ) {
    setDownloadingId(file.id);

    try {
      const downloaded =
        await api
          .downloadPrivateClinicalFile(
            contactId,
            file.id
          );

      const objectUrl =
        URL.createObjectURL(
          downloaded.blob
        );

      const anchor =
        document.createElement("a");

      anchor.href = objectUrl;
      anchor.download =
        downloaded.filename;

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(objectUrl);

      show(
        "Descarga clínica preparada.",
        "success"
      );
    } catch (error) {
      if (
        error instanceof ApiClientError
      ) {
        show(error.message, "error");
      } else {
        show(
          "No se pudo descargar el archivo.",
          "error"
        );
      }
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div>
      <div className="chart-list-actions">
        <h3 style={{ margin: 0, fontSize: 14 }}>Archivos clínicos</h3>
        {!open && (
          <button className="btn-ghost" onClick={() => setOpen(true)}>
            + Registrar documento
          </button>
        )}
      </div>

      {open && (
        <div className="chart-encounter-card">
          <div className="chart-notice" style={{ marginBottom: 8 }}>
            🔒 El contenido se almacena de forma privada, se valida contra
            el tipo declarado y se registra su integridad digital.
          </div>
          <div className="chart-form">
            <div className="chart-form-row">
              <select value={fileType} onChange={(e: any) => setFileType(e.target.value)}>
                {FILE_TYPES
                  .filter(
                    (type) =>
                      type.code !== "VIDEO"
                  )
                  .map((type) => (
                    <option
                      key={type.code}
                      value={type.code}
                    >
                      {type.label}
                    </option>
                  ))}
              </select>

              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={(event: any) =>
                  setSelectedFile(
                    event.target.files?.[0] ||
                    null
                  )
                }
              />
            </div>

            <input
              placeholder="Etiqueta clínica (opcional)"
              value={label}
              onChange={(event: any) =>
                setLabel(event.target.value)
              }
            />

            {programId && (
              <>
                <div className="chart-form-row">
                  <select
                    value={sessionId}
                    onChange={(event: any) =>
                      setSessionId(
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      Sin sesión específica
                    </option>
                    {sessions.map(
                      (session) => (
                        <option
                          key={session.id}
                          value={String(
                            session.id
                          )}
                        >
                          Sesión {
                            session.session_number
                          } · {
                            session.session_type
                          }
                        </option>
                      )
                    )}
                  </select>

                  <select
                    value={capturePhase}
                    onChange={(event: any) =>
                      setCapturePhase(
                        event.target.value
                      )
                    }
                  >
                    <option value="BEFORE">
                      Antes
                    </option>
                    <option value="DURING">
                      Durante
                    </option>
                    <option value="AFTER">
                      Después
                    </option>
                    <option value="FOLLOW_UP">
                      Seguimiento
                    </option>
                  </select>
                </div>

                <label className="meta">
                  <input
                    type="checkbox"
                    checked={comparisonReady}
                    onChange={(event: any) =>
                      setComparisonReady(
                        event.target.checked
                      )
                    }
                  />{" "}
                  Disponible para comparaciones
                  clínicas
                </label>
              </>
            )}

            <select
              value={patientVisibility}
              onChange={(event: any) =>
                setPatientVisibility(
                  event.target.value
                )
              }
            >
              <option value="INTERNAL">
                Solo uso clínico interno
              </option>
              <option value="SHAREABLE">
                Preparado para compartir
              </option>
              <option value="PUBLISHED">
                Publicado al paciente
              </option>
            </select>

            {selectedFile && (
              <div className="meta">
                {selectedFile.name} · {
                  (
                    selectedFile.size /
                    1024 /
                    1024
                  ).toFixed(2)
                } MB
              </div>
            )}

            <div className="row">
              <button className="btn-primary" disabled={submitting || !selectedFile} onClick={submit}>
                {submitting ? "Cargando…" : "Subir archivo privado"}
              </button>
              <button className="btn-ghost" onClick={() => setOpen(false)}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {files.length === 0 && !open && <div className="chart-empty">Sin documentos registrados.</div>}
      {files.length > 0 && (
        <table className="task-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Etiqueta</th>
              <th>Visibilidad</th>
              <th>Estado</th>
              <th>Fecha</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id}>
                <td>{FILE_TYPES.find((t) => t.code === f.file_type)?.label || f.file_type}</td>
                <td>{f.label || "-"}</td>
                <td>
                  {f.patient_visibility ||
                    f.clinical_visibility}
                </td>
                <td>
                  {f.managed_storage
                    ? f.content_status ||
                      "UPLOADING"
                    : "LEGACY"}
                </td>
                <td>
                  {formatDateTime(
                    f.captured_at ||
                    f.created_at
                  )}
                </td>
                <td>
                  {f.managed_storage &&
                  f.content_status ===
                    "AVAILABLE" ? (
                    <button
                      className="btn-ghost"
                      disabled={
                        downloadingId === f.id
                      }
                      onClick={() =>
                        download(f)
                      }
                    >
                      {downloadingId === f.id
                        ? "Descargando…"
                        : "Descargar"}
                    </button>
                  ) : (
                    <span className="meta">
                      Sin descarga privada
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Tratamiento — Frontend Operativo de Fase 2 + Fase 2B (EN DESARROLLO,
// 2026-08-23). Ejecución: precarga la plantilla del servicio resuelto
// (encounter.opportunity_id → opportunities.service_id, con override
// manual cuando no hay oportunidad) y deja registrar el consumo REAL —
// eso es lo que produce el descuento de stock y el consumo del
// cabezal/equipo, nunca lo teórico. Muestra "esperado X / utilizado Y"
// por línea sin impedir que la realidad clínica difiera de la plantilla,
// y separa visualmente costo de insumos vs. costo de equipamiento vs.
// costo total (insumos_cost/equipment_cost/total_cost, ya devueltos por
// el backend).
// ---------------------------------------------------------------------

interface DraftInsumoRow {
  key: string;
  product_id: string;
  product_name: string;
  requires_lot: boolean | null; // null = no lo sabemos todavía (línea agregada manualmente, sin lookup)
  quantity: string;
  warehouse_id: string;
  lot_id: string;
  unit_cost: string;
  lots: { lot_id: Id; lot_number: string | null; serial_number: string | null; unit_cost: string | number; warehouse_id: Id; available_quantity: string | number }[];
}

interface DraftEquipmentRow {
  key: string;
  asset_id: string;
  asset_name: string;
  capacity_unit: string;
  quantity: string;
  component_id: string;
  component_missing: boolean;
}

function newRowKey() {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function TreatmentPanel({ encounter }: { encounter: ClinicalEncounter | null }) {
  const { show } = useToast();
  const [treatment, setTreatment] = useState<TreatmentRecordView | null>(null);
  const [loading, setLoading] = useState(false);
  const [servicesLookup, setServicesLookup] = useState<{ id: Id; code: string; name: string }[]>([]);
  const [overrideServiceId, setOverrideServiceId] = useState("");
  const [warehouses, setWarehouses] = useState<{ id: Id; code: string; name: string }[]>([]);
  const [products, setProducts] = useState<{ id: Id; sku: string; name: string; lot_control: boolean; serial_control: boolean }[]>([]);
  const [assets, setAssets] = useState<{ id: Id; asset_tag: string; name: string }[]>([]);

  const [draftInsumos, setDraftInsumos] = useState<DraftInsumoRow[]>([]);
  const [draftEquipment, setDraftEquipment] = useState<DraftEquipmentRow[]>([]);
  const [submitting, setSubmitting] = useState(false);

  async function load(serviceOverride?: string) {
    if (!encounter) return;
    setLoading(true);
    try {
      const [tr, wh] = await Promise.all([
        api.getTreatmentRecord(encounter.id, serviceOverride ? Number(serviceOverride) : undefined),
        warehouses.length ? Promise.resolve(warehouses) : api.getWarehousesLookup(),
      ]);
      setTreatment(tr);
      setWarehouses(wh);
      seedDraftsFromExpected(tr);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo cargar el registro de tratamiento.", "error");
    } finally {
      setLoading(false);
    }
  }

  function seedDraftsFromExpected(tr: TreatmentRecordView) {
    if (tr.expected?.consumption_template) {
      setDraftInsumos(
        tr.expected.consumption_template.items.map((it) => ({
          key: newRowKey(),
          product_id: String(it.product_id),
          product_name: it.product_name,
          requires_lot: null,
          quantity: String(it.expected_quantity),
          warehouse_id: "",
          lot_id: "",
          unit_cost: "",
          lots: [],
        }))
      );
    } else {
      setDraftInsumos([]);
    }
    if (tr.expected?.equipment_template) {
      setDraftEquipment(
        tr.expected.equipment_template.items.map((it) => ({
          key: newRowKey(),
          asset_id: String(it.asset_id),
          asset_name: it.asset_name,
          capacity_unit: it.capacity_unit,
          quantity: String(it.expected_quantity),
          component_id: "",
          component_missing: false,
        }))
      );
      // Resolver el componente activo de cada asset — necesario para
      // completar component_id sin que el profesional tenga que saber
      // cuál cabezal está físicamente instalado ahora.
      tr.expected.equipment_template.items.forEach((it) => {
        api
          .getEquipmentComponentsLookup(it.asset_id)
          .then((comps) => {
            setDraftEquipment((prev) =>
              prev.map((row) =>
                row.asset_id === String(it.asset_id) && !row.component_id
                  ? { ...row, component_id: comps[0] ? String(comps[0].id) : "", component_missing: !comps[0] }
                  : row
              )
            );
          })
          .catch(() => {});
      });
    } else {
      setDraftEquipment([]);
    }
  }

  useEffect(() => {
    setTreatment(null);
    setOverrideServiceId("");
    setDraftInsumos([]);
    setDraftEquipment([]);
    if (encounter) load();
    api.getServicesLookup().then(setServicesLookup).catch(() => {});
    // Se cargan de entrada (no sólo perezosamente al enfocar un <select>):
    // las filas precargadas desde la plantilla necesitan `products`/`assets`
    // ya disponibles para poder renderizar el <select> editable en vez de
    // quedarse en el <span> de sólo lectura — si no, una fila con producto
    // ya conocido (por la plantilla) nunca ofrece cambiarlo.
    ensureCatalogsLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounter?.id]);

  function ensureCatalogsLoaded() {
    if (!products.length) api.getInventoryProductsLookup().then(setProducts).catch(() => {});
    if (!assets.length) api.getEquipmentAssetsLookup().then(setAssets).catch(() => {});
  }

  async function loadLotsFor(rowKey: string, productId: string) {
    if (!productId) return;
    try {
      const lots = await api.getInventoryLotsLookup(Number(productId));
      setDraftInsumos((prev) => prev.map((r) => (r.key === rowKey ? { ...r, lots } : r)));
    } catch {
      // silencioso — el selector queda vacío, el submit igual valida server-side
    }
  }

  function updateInsumoRow(key: string, patch: Partial<DraftInsumoRow>) {
    setDraftInsumos((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function updateEquipmentRow(key: string, patch: Partial<DraftEquipmentRow>) {
    setDraftEquipment((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addInsumoRow() {
    ensureCatalogsLoaded();
    setDraftInsumos((prev) => [
      ...prev,
      { key: newRowKey(), product_id: "", product_name: "", requires_lot: null, quantity: "", warehouse_id: "", lot_id: "", unit_cost: "", lots: [] },
    ]);
  }

  function addEquipmentRow() {
    ensureCatalogsLoaded();
    setDraftEquipment((prev) => [
      ...prev,
      { key: newRowKey(), asset_id: "", asset_name: "", capacity_unit: "", quantity: "", component_id: "", component_missing: false },
    ]);
  }

  async function submit() {
    if (!encounter) return;
    const insumos: TreatmentRecordInsumoInput[] = [];
    for (const row of draftInsumos) {
      if (!row.product_id || !row.quantity || Number(row.quantity) <= 0) continue;
      if (!row.warehouse_id) {
        show(`Falta elegir depósito para ${row.product_name || "un insumo"}.`, "error");
        return;
      }
      const product = products.find((p) => String(p.id) === row.product_id);
      const requiresLot = row.requires_lot ?? (product ? product.lot_control || product.serial_control : false);
      if (requiresLot && !row.lot_id) {
        show(`Falta elegir lote/serie para ${row.product_name || "un insumo"}.`, "error");
        return;
      }
      if (!requiresLot && !row.unit_cost) {
        show(`Este insumo no controla lote — informá el costo unitario.`, "error");
        return;
      }
      insumos.push({
        product_id: Number(row.product_id),
        warehouse_id: Number(row.warehouse_id),
        quantity: Number(row.quantity),
        lot_id: requiresLot ? Number(row.lot_id) : undefined,
        unit_cost: !requiresLot ? Number(row.unit_cost) : undefined,
      });
    }
    const equipmentUsage: TreatmentRecordEquipmentInput[] = [];
    for (const row of draftEquipment) {
      if (!row.asset_id || !row.quantity || Number(row.quantity) <= 0) continue;
      equipmentUsage.push({
        asset_id: Number(row.asset_id),
        component_id: row.component_id ? Number(row.component_id) : undefined,
        quantity: Number(row.quantity),
      });
    }
    if (!insumos.length && !equipmentUsage.length) {
      show("No hay ningún consumo real cargado para confirmar.", "error");
      return;
    }
    const idempotencyKey =
      typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `treat-${Date.now()}-${Math.random()}`;
    setSubmitting(true);
    try {
      await api.registerTreatmentRecord(encounter.id, { idempotency_key: idempotencyKey, insumos, equipment_usage: equipmentUsage });
      show("Consumo real registrado.", "success");
      await load(overrideServiceId || undefined);
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar el consumo.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (!encounter) {
    return <div className="chart-empty">No hay una consulta en curso. Abrí un encuentro para registrar tratamiento.</div>;
  }
  if (loading && !treatment) {
    return <div className="loading-strip">Cargando registro de tratamiento...</div>;
  }
  if (!treatment) {
    return <div className="chart-empty">No se pudo cargar el registro de tratamiento.</div>;
  }

  const expected = treatment.expected;

  return (
    <div>
      <div className="chart-list-actions">
        <h3 style={{ margin: 0, fontSize: 14 }}>Tratamiento</h3>
      </div>

      {expected ? (
        <p className="meta">
          Servicio: <b>{expected.service_name || `#${expected.service_id}`}</b>{" "}
          {expected.resolved_via === "opportunity" ? "(resuelto por la oportunidad de origen)" : "(elegido manualmente)"}
        </p>
      ) : (
        <div className="chart-encounter-card">
          <p className="meta">
            Este encuentro no tiene una oportunidad de origen con servicio asociado — no hay plantilla para precargar. Podés elegir el
            servicio manualmente para ver su plantilla (esto no cambia el encuentro, sólo ayuda a precargar cantidades esperadas):
          </p>
          <div className="row">
            <select value={overrideServiceId} onChange={(e: any) => setOverrideServiceId(e.target.value)}>
              <option value="">Servicio...</option>
              {servicesLookup.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <button className="btn-ghost" disabled={!overrideServiceId} onClick={() => load(overrideServiceId)}>
              Precargar plantilla
            </button>
          </div>
        </div>
      )}

      {expected?.consumption_template && (
        <div className="chart-encounter-card">
          <h4 style={{ fontSize: 13, marginTop: 0 }}>Insumos — esperado vs. utilizado</h4>
          {expected.consumption_template.items.length === 0 && <p className="meta">Este servicio no tiene consumo de insumos esperado.</p>}
          {expected.consumption_template.items.map((it) => (
            <div key={it.id} className="meta">
              {it.product_name}: esperado {it.expected_quantity} / utilizado{" "}
              <b style={it.used_quantity > it.expected_quantity ? { color: "#b45309" } : undefined}>{it.used_quantity}</b>
              {it.expected_cost !== null && (
                <span style={{ marginLeft: 6, opacity: 0.7 }}>(costo esperado: ${it.expected_cost.toLocaleString("es-AR")})</span>
              )}
            </div>
          ))}
        </div>
      )}

      {expected?.equipment_template && (
        <div className="chart-encounter-card">
          <h4 style={{ fontSize: 13, marginTop: 0 }}>Equipamiento — esperado vs. utilizado</h4>
          {expected.equipment_template.items.length === 0 && <p className="meta">Este servicio no tiene uso de equipamiento esperado.</p>}
          {expected.equipment_template.items.map((it) => (
            <div key={it.id} className="meta">
              {it.asset_name}: esperado {it.expected_quantity} {it.capacity_unit} / utilizados{" "}
              <b style={it.used_quantity > Number(it.expected_quantity) ? { color: "#b45309" } : undefined}>
                {it.used_quantity} {it.capacity_unit}
              </b>
              {it.expected_cost !== null && (
                <span style={{ marginLeft: 6, opacity: 0.7 }}>(costo esperado: ${it.expected_cost.toLocaleString("es-AR")})</span>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="chart-encounter-card">
        <h4 style={{ fontSize: 13, marginTop: 0 }}>Registrar consumo real — insumos</h4>
        {draftInsumos.map((row) => {
          const product = products.find((p) => String(p.id) === row.product_id);
          const requiresLot = row.requires_lot ?? (product ? product.lot_control || product.serial_control : null);
          return (
            <div className="chart-item-row" key={row.key}>
              {row.product_name && !products.length ? (
                <span>{row.product_name}</span>
              ) : (
                <select
                  value={row.product_id}
                  onFocus={ensureCatalogsLoaded}
                  onChange={(e: any) => {
                    const p = products.find((x) => String(x.id) === e.target.value);
                    updateInsumoRow(row.key, {
                      product_id: e.target.value,
                      product_name: p?.name || "",
                      requires_lot: p ? p.lot_control || p.serial_control : null,
                      lot_id: "",
                      unit_cost: "",
                    });
                    if (p && (p.lot_control || p.serial_control)) loadLotsFor(row.key, e.target.value);
                  }}
                >
                  <option value="">{row.product_name || "Producto..."}</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="number"
                min="0"
                step="any"
                placeholder="Cantidad"
                value={row.quantity}
                onChange={(e: any) => updateInsumoRow(row.key, { quantity: e.target.value })}
              />
              <select value={row.warehouse_id} onChange={(e: any) => updateInsumoRow(row.key, { warehouse_id: e.target.value })}>
                <option value="">Depósito...</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
              {requiresLot ? (
                <select value={row.lot_id} onChange={(e: any) => updateInsumoRow(row.key, { lot_id: e.target.value })}>
                  <option value="">Lote/serie...</option>
                  {row.lots.map((l) => (
                    <option key={l.lot_id} value={l.lot_id}>
                      {l.lot_number || l.serial_number || `#${l.lot_id}`} (disp. {l.available_quantity})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Costo unitario"
                  value={row.unit_cost}
                  onChange={(e: any) => updateInsumoRow(row.key, { unit_cost: e.target.value })}
                />
              )}
              <button className="btn-ghost" onClick={() => setDraftInsumos((prev) => prev.filter((r) => r.key !== row.key))}>
                ✕
              </button>
            </div>
          );
        })}
        <button className="btn-ghost" onClick={addInsumoRow}>
          + Agregar insumo
        </button>
      </div>

      <div className="chart-encounter-card">
        <h4 style={{ fontSize: 13, marginTop: 0 }}>Registrar consumo real — equipamiento</h4>
        {draftEquipment.map((row) => (
          <div className="chart-item-row" key={row.key}>
            {row.asset_name && !assets.length ? (
              <span>{row.asset_name}</span>
            ) : (
              <select
                value={row.asset_id}
                onFocus={ensureCatalogsLoaded}
                onChange={(e: any) => {
                  const a = assets.find((x) => String(x.id) === e.target.value);
                  updateEquipmentRow(row.key, { asset_id: e.target.value, asset_name: a?.name || "", component_id: "" });
                  if (e.target.value) {
                    api
                      .getEquipmentComponentsLookup(Number(e.target.value))
                      .then((comps) =>
                        updateEquipmentRow(row.key, { component_id: comps[0] ? String(comps[0].id) : "", component_missing: !comps[0] })
                      )
                      .catch(() => {});
                  }
                }}
              >
                <option value="">{row.asset_name || "Equipo..."}</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
            <input
              type="number"
              min="0"
              step="any"
              placeholder={row.capacity_unit ? `Cantidad (${row.capacity_unit})` : "Cantidad"}
              value={row.quantity}
              onChange={(e: any) => updateEquipmentRow(row.key, { quantity: e.target.value })}
            />
            {row.component_missing && <span className="pill pill-bad">Sin componente instalado</span>}
            <button className="btn-ghost" onClick={() => setDraftEquipment((prev) => prev.filter((r) => r.key !== row.key))}>
              ✕
            </button>
          </div>
        ))}
        <button className="btn-ghost" onClick={addEquipmentRow}>
          + Agregar uso de equipo
        </button>
      </div>

      <div className="row">
        <button className="btn-primary" disabled={submitting} onClick={submit}>
          Confirmar consumo real
        </button>
      </div>

      <div className="chart-encounter-card">
        <h4 style={{ fontSize: 13, marginTop: 0 }}>Costo real del tratamiento</h4>
        <div className="meta">Insumos: ${treatment.totals.insumos_cost.toLocaleString("es-AR")}</div>
        <div className="meta">Equipamiento: ${treatment.totals.equipment_cost.toLocaleString("es-AR")}</div>
        <div>
          <b>Total: ${treatment.totals.total_cost.toLocaleString("es-AR")}</b>
        </div>
      </div>

      {(expected?.consumption_template || expected?.equipment_template) && (() => {
        const consumptionExpected = expected.consumption_template?.expected_cost_total ?? 0;
        const equipmentExpected = expected.equipment_template?.expected_cost_total ?? 0;
        const expectedTotal = consumptionExpected + equipmentExpected;
        const realTotal = treatment.totals.total_cost;
        const deviation = realTotal - expectedTotal;
        const deviationPct = expectedTotal > 0 ? (deviation / expectedTotal) * 100 : null;
        const isComplete =
          (expected.consumption_template?.expected_cost_complete ?? true) &&
          (expected.equipment_template?.expected_cost_complete ?? true);
        const deviationColor = deviation > 0 ? "#b45309" : deviation < 0 ? "#15803d" : undefined;
        const sign = deviation > 0 ? "+" : "";
        return (
          <div className="chart-encounter-card">
            <h4 style={{ fontSize: 13, marginTop: 0 }}>Esperado vs. real — costo y desvío</h4>
            <div className="meta">Costo esperado (según plantilla vigente): ${expectedTotal.toLocaleString("es-AR")}</div>
            <div className="meta">Costo real registrado: ${realTotal.toLocaleString("es-AR")}</div>
            <div>
              <b style={deviationColor ? { color: deviationColor } : undefined}>
                Desvío: {sign}${deviation.toLocaleString("es-AR")}
                {deviationPct !== null && ` (${sign}${deviationPct.toFixed(1)}%)`}
              </b>
            </div>
            {!isComplete && (
              <p className="meta" style={{ marginTop: 6 }}>
                El costo esperado es parcial: hay insumos sin control de lote en la plantilla, que no tienen un costo de referencia
                almacenado (la política de costeo exige indicarlo manualmente en cada registro real). El desvío mostrado subestima el
                costo esperado real.
              </p>
            )}
            <p className="meta" style={{ marginTop: 6 }}>
              Este desvío es informativo — no genera alertas ni reglas todavía. La plantilla es una sugerencia operativa: nunca se
              descuenta stock ni se registra costo por tener un turno o una plantilla; sólo la confirmación real genera movimientos.
            </p>
          </div>
        );
      })()}

      <div className="chart-encounter-card">
        <h4 style={{ fontSize: 13, marginTop: 0 }}>Ya registrado en este encuentro</h4>
        {treatment.insumos.length === 0 && treatment.equipment_usage.length === 0 && (
          <p className="meta">Todavía no se registró ningún consumo real.</p>
        )}
        {treatment.insumos.map((c) => (
          <div className="meta" key={`c-${c.id}`}>
            {c.product_name}: {c.quantity} · {c.warehouse_name} · ${Number(c.total_cost).toLocaleString("es-AR")} · {formatDateTime(c.created_at)}
          </div>
        ))}
        {treatment.equipment_usage.map((u) => (
          <div className="meta" key={`u-${u.id}`}>
            {u.asset_name}: {u.quantity} {u.capacity_unit} · ${Number(u.total_cost).toLocaleString("es-AR")} · {formatDateTime(u.created_at)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Comparativas — resumen general + evolución antropométrica real desde
// que existe `anthropometric_measurements` (Fase 1B, frontend longitudinal
// posterior al backend, 2026-08-23). Sigue siendo honesto sobre lo que
// falta: `clinical_comparison_snapshots` (Migración 14) es puro schema-
// cache sin job de recomputación todavía — acá se muestra la fuente real
// (las mediciones una por una), no un snapshot recalculado.
// ---------------------------------------------------------------------
const CLINICAL_REPORT_SECTION_OPTIONS: Array<{
  code: Clinical360ReportSection;
  label: string;
}> = [
  { code: "goals", label: "Objetivos clínicos" },
  { code: "plans", label: "Planes asistenciales" },
  { code: "sessions", label: "Sesiones" },
  { code: "outcomes", label: "Resultados y evolución" },
  { code: "actions", label: "Acciones pendientes" },
  {
    code: "administrations",
    label: "Administraciones y trazabilidad",
  },
  { code: "files", label: "Archivos clínicos" },
  { code: "anthropometry", label: "Antropometría" },
  {
    code: "comparisons",
    label: "Comparaciones clínicas",
  },
  {
    code: "anatomical_maps",
    label: "Mapas anatómicos",
  },
];


function ClinicalReportsPanel({
  detail,
  loading,
  defaultProfessionalId,
  onChanged,
}: {
  detail:
    ClinicalCareProgramDetailResponse | null;
  loading: boolean;
  defaultProfessionalId: string;
  onChanged: () => void | Promise<void>;
}) {
  const { show } = useToast();
  const [creating, setCreating] =
    useState(false);
  const [submitting, setSubmitting] =
    useState(false);
  const [editingReportId, setEditingReportId] =
    useState<Id | null>(null);
  const [downloadingId, setDownloadingId] =
    useState<Id | null>(null);
  const [signingId, setSigningId] =
    useState<Id | null>(null);
  const [title, setTitle] = useState("");
  const [purpose, setPurpose] =
    useState("");
  const [clinicalSummary, setClinicalSummary] =
    useState("");
  const [
    professionalConclusion,
    setProfessionalConclusion,
  ] = useState("");
  const [
    patientVisibility,
    setPatientVisibility,
  ] = useState<"INTERNAL" | "SHAREABLE">(
    "INTERNAL"
  );
  const [
    selectedSections,
    setSelectedSections,
  ] = useState<Clinical360ReportSection[]>(
    CLINICAL_REPORT_SECTION_OPTIONS.map(
      (option) => option.code
    )
  );

  const reports: Clinical360Report[] =
    detail?.reports || [];

  function startEditing(
    report: Clinical360Report
  ) {
    setEditingReportId(report.id);
    setTitle(report.title);
    setPurpose(report.purpose || "");
    setClinicalSummary(
      report.clinical_summary || ""
    );
    setProfessionalConclusion(
      report.professional_conclusion || ""
    );
    setPatientVisibility(
      report.patient_visibility === "SHAREABLE"
        ? "SHAREABLE"
        : "INTERNAL"
    );
    setSelectedSections([
      ...report.included_sections,
    ]);
    setCreating(true);
  }

  async function createReport() {
    if (!detail) return;

    if (!title.trim()) {
      show(
        "Ingresá el título del informe.",
        "error"
      );
      return;
    }

    if (!defaultProfessionalId) {
      show(
        "No se pudo identificar al profesional autor.",
        "error"
      );
      return;
    }

    if (selectedSections.length === 0) {
      show(
        "Seleccioná al menos una sección clínica.",
        "error"
      );
      return;
    }

    setSubmitting(true);

    try {
      if (editingReportId) {
        await api.updateClinical360Report(
          detail.program.id,
          editingReportId,
          {
            title: title.trim(),
            purpose:
              purpose.trim() || undefined,
            clinical_summary:
              clinicalSummary.trim() ||
              undefined,
            professional_conclusion:
              professionalConclusion.trim() ||
              undefined,
            included_sections:
              selectedSections,
            patient_visibility:
              patientVisibility,
          }
        );
      } else {
        await api.createClinical360Report(
          detail.program.id,
          {
            report_type: "CLINICAL_360",
            title: title.trim(),
            purpose:
              purpose.trim() || undefined,
            clinical_summary:
              clinicalSummary.trim() ||
              undefined,
            professional_conclusion:
              professionalConclusion.trim() ||
              undefined,
            included_sections:
              selectedSections,
            professional_id:
              defaultProfessionalId,
            patient_visibility:
              patientVisibility,
          }
        );
      }

      show(
        editingReportId
          ? "Borrador actualizado y snapshot regenerado."
          : "Informe Clinical 360 creado en borrador.",
        "success"
      );

      resetForm();
      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo crear el informe clínico.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function signReport(
    report: Clinical360Report
  ) {
    if (!detail) return;

    if (!window.confirm(
      "Al firmar, el informe quedará inmutable. ¿Confirmás la firma profesional?"
    )) return;

    setSigningId(report.id);

    try {
      await api.signClinical360Report(
        detail.program.id,
        report.id
      );
      show(
        "Informe Clinical 360 firmado.",
        "success"
      );
      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo firmar el informe clínico.",
        "error"
      );
    } finally {
      setSigningId(null);
    }
  }

  async function downloadReport(
    report: Clinical360Report,
    format: "html" | "json"
  ) {
    if (!detail) return;

    setDownloadingId(report.id);

    try {
      const downloaded =
        await api.downloadClinical360Report(
          detail.program.id,
          report.id,
          format
        );

      const objectUrl =
        URL.createObjectURL(
          downloaded.blob
        );
      const anchor =
        document.createElement("a");

      anchor.href = objectUrl;
      anchor.download = downloaded.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);

      show(
        format === "html"
          ? "Informe descargado para imprimir o guardar como PDF."
          : "Respaldo estructurado descargado.",
        "success"
      );
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo descargar el informe clínico.",
        "error"
      );
    } finally {
      setDownloadingId(null);
    }
  }

  function resetForm() {
    setTitle("");
    setPurpose("");
    setClinicalSummary("");
    setProfessionalConclusion("");
    setPatientVisibility("INTERNAL");
    setSelectedSections(
      CLINICAL_REPORT_SECTION_OPTIONS.map(
        (option) => option.code
      )
    );
    setEditingReportId(null);
    setCreating(false);
  }

  function toggleSection(
    section: Clinical360ReportSection
  ) {
    setSelectedSections((current) =>
      current.includes(section)
        ? current.filter(
            (item) => item !== section
          )
        : [...current, section]
    );
  }

  if (loading) {
    return (
      <div className="chart-loading">
        Cargando informes clínicos…
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="chart-empty">
        Seleccioná un programa clínico para
        consultar sus informes.
      </div>
    );
  }

  return (
    <div className="chart-panel">
      <div className="chart-list-actions">
        <div>
          <h3 style={{ margin: 0 }}>
            Informes Clinical 360
          </h3>
          <div className="meta">
            Documentos versionados, firmables
            y verificables.
          </div>
        </div>
        {!creating && (
          <button
            className="btn-primary"
            onClick={() =>
              setCreating(true)
            }
          >
            + Nuevo informe
          </button>
        )}
      </div>

      {creating && (
        <div
          className="chart-encounter-card"
          style={{ marginTop: 12 }}
        >
          <div className="chart-form">
            <input
              placeholder="Título del informe"
              value={title}
              onChange={(event: any) =>
                setTitle(event.target.value)
              }
            />

            <input
              placeholder="Finalidad del informe (opcional)"
              value={purpose}
              onChange={(event: any) =>
                setPurpose(event.target.value)
              }
            />

            <textarea
              rows={3}
              placeholder="Resumen clínico (opcional)"
              value={clinicalSummary}
              onChange={(event: any) =>
                setClinicalSummary(
                  event.target.value
                )
              }
            />

            <textarea
              rows={4}
              placeholder="Conclusión profesional requerida para firmar"
              value={professionalConclusion}
              onChange={(event: any) =>
                setProfessionalConclusion(
                  event.target.value
                )
              }
            />

            <select
              value={patientVisibility}
              onChange={(event: any) =>
                setPatientVisibility(
                  event.target.value
                )
              }
            >
              <option value="INTERNAL">
                Solo uso clínico interno
              </option>
              <option value="SHAREABLE">
                Preparado para compartir
              </option>
            </select>

            <fieldset
              style={{
                border:
                  "1px solid rgba(97, 78, 67, 0.16)",
                borderRadius: 10,
                padding: 12,
              }}
            >
              <legend>
                Secciones incluidas
              </legend>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(210px, 1fr))",
                  gap: 8,
                }}
              >
                {CLINICAL_REPORT_SECTION_OPTIONS.map(
                  (option) => (
                    <label
                      className="meta"
                      key={option.code}
                    >
                      <input
                        type="checkbox"
                        checked={
                          selectedSections.includes(
                            option.code
                          )
                        }
                        onChange={() =>
                          toggleSection(
                            option.code
                          )
                        }
                      />{" "}
                      {option.label}
                    </label>
                  )
                )}
              </div>
            </fieldset>

            <div className="row">
              <button
                className="btn-primary"
                disabled={submitting}
                onClick={createReport}
              >
                {submitting
                  ? editingReportId
                    ? "Guardando…"
                    : "Creando…"
                  : editingReportId
                    ? "Guardar cambios"
                    : "Crear borrador"}
              </button>
              <button
                className="btn-ghost"
                disabled={submitting}
                onClick={resetForm}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {reports.length === 0 && !creating && (
        <div className="chart-empty">
          No hay informes registrados para
          este programa.
        </div>
      )}

      {reports.map((report) => {
        const signed =
          report.status === "SIGNED";
        const busy =
          String(signingId) === String(report.id) ||
          String(downloadingId) === String(report.id);

        return (
          <article
            className="chart-encounter-card"
            style={{ marginTop: 12 }}
            key={report.id}
          >
            <div className="chart-list-actions">
              <div>
                <h4
                  style={{
                    margin: 0,
                    fontSize: 15,
                  }}
                >
                  {report.title}
                </h4>
                <div className="meta">
                  Versión {report.version_number}
                  {" · "}
                  {signed ? "Firmado" : "Borrador"}
                  {" · "}
                  {report.professional_name ||
                    "Profesional no informado"}
                </div>
              </div>
              <span
                className={
                  signed
                    ? "pill pill-good"
                    : "pill"
                }
              >
                {report.status}
              </span>
            </div>

            {report.purpose && (
              <p className="meta">
                <strong>Finalidad:</strong>{" "}
                {report.purpose}
              </p>
            )}

            {report.professional_conclusion && (
              <p>
                <strong>Conclusión:</strong>{" "}
                {report.professional_conclusion}
              </p>
            )}

            <div className="meta">
              Secciones: {report.included_sections
                .map(
                  (section) =>
                    CLINICAL_REPORT_SECTION_OPTIONS.find(
                      (option) =>
                        option.code === section
                    )?.label || section
                )
                .join(" · ")}
            </div>

            {signed && (
              <div
                className="meta"
                style={{ marginTop: 6 }}
              >
                Firmado {report.signed_at
                  ? formatDateTime(report.signed_at)
                  : "sin fecha informada"}
                {report.content_hash
                  ? ` · SHA-256 ${report.content_hash.slice(0, 12)}…`
                  : ""}
              </div>
            )}

            <div
              className="row"
              style={{ marginTop: 12 }}
            >
              {!signed && (
                <>
                  <button
                    className="btn-ghost"
                    disabled={busy}
                    onClick={() =>
                      startEditing(report)
                    }
                  >
                    Editar borrador
                  </button>
                  <button
                    className="btn-primary"
                    disabled={busy}
                    onClick={() =>
                      signReport(report)
                    }
                  >
                    {String(signingId) === String(report.id)
                      ? "Firmando…"
                      : "Firmar informe"}
                  </button>
                </>
              )}


              {signed && (
                <>
                  <button
                    className="btn-primary"
                    disabled={busy}
                    onClick={() =>
                      downloadReport(
                        report,
                        "html"
                      )
                    }
                  >
                    Descargar / imprimir
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={busy}
                    onClick={() =>
                      downloadReport(
                        report,
                        "json"
                      )
                    }
                  >
                    Descargar JSON
                  </button>
                </>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}


function ComparativesPanel({
  record,
  prescriptions,
  orders,
  consents,
  files,
  measurements,
  programDetail,
  programLoading,
  onProgramChanged,
}: {
  record: ClinicalRecordType;
  prescriptions: Prescription[];
  orders: MedicalOrder[];
  consents: PatientConsent[];
  files: PatientFile[];
  measurements: AnthropometricMeasurement[];
  programDetail:
    ClinicalCareProgramDetailResponse | null;
  programLoading: boolean;
  onProgramChanged:
    () => void | Promise<void>;
}) {
  const totalNotes = record.encounters.reduce((acc, e) => acc + (e.notes?.length || 0), 0);
  const signedNotes = record.encounters.reduce((acc, e) => acc + (e.notes || []).filter((n) => n.status === "SIGNED").length, 0);

  const stats = [
    { label: "Encuentros clínicos", value: record.encounters.length },
    { label: "Notas (firmadas / total)", value: `${signedNotes} / ${totalNotes}` },
    { label: "Recetas", value: prescriptions.length },
    { label: "Órdenes médicas", value: orders.length },
    { label: "Consentimientos firmados", value: consents.filter((c) => c.status === "SIGNED").length },
    { label: "Archivos clínicos", value: files.length },
  ];

  const sortedMeasurements = [...measurements].sort(
    (a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime()
  );

  return (
    <div>
      <div
        className="chart-list-actions"
        style={{ marginBottom: 12 }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 16,
            }}
          >
            Evolución clínica 360°
          </h3>
          <div className="meta">
            Fuentes clínicas trazables del proceso
            asistencial seleccionado.
          </div>
        </div>
        {programLoading && (
          <span className="meta">
            Actualizando…
          </span>
        )}
      </div>

      {programDetail && (
        <>
          <div
            className="kpi-cards"
            style={{
              gridTemplateColumns:
                "repeat(3, 1fr)",
              marginBottom: 16,
            }}
          >
            {[
              {
                label: "Sesiones",
                value:
                  programDetail.sessions
                    .length,
              },
              {
                label:
                  "Administraciones",
                value:
                  programDetail
                    .administrations.length,
              },
              {
                label:
                  "Archivos comparables",
                value:
                  programDetail.files
                    .filter(
                      (file) =>
                        file.comparison_ready
                    ).length,
              },
              {
                label: "Mediciones",
                value:
                  programDetail
                    .anthropometry.length,
              },
              {
                label: "Comparaciones",
                value:
                  programDetail
                    .comparisons.length,
              },
              {
                label: "Mapas clínicos",
                value:
                  programDetail
                    .anatomical_maps.length,
              },
            ].map((item) => (
              <div
                className="kpi-card"
                key={item.label}
              >
                <div className="muted">
                  {item.label}
                </div>
                <div className="big">
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          <div
            className="chart-encounter-card"
            style={{ marginBottom: 16 }}
          >
            <h4
              style={{
                marginTop: 0,
                marginBottom: 6,
              }}
            >
              {
                programDetail.program
                  .title
              }
            </h4>
            <div className="meta">
              {
                programDetail.program
                  .clinical_indication ||
                "Sin indicación clínica consignada."
              }
            </div>
            <div
              className="meta"
              style={{ marginTop: 5 }}
            >
              Estado:{" "}
              {
                programDetail.program
                  .status
              }
              {" · "}Profesional:{" "}
              {
                programDetail.program
                  .responsible_professional_name ||
                "No informado"
              }
            </div>
          </div>
        </>
      )}

      {!programDetail &&
        !programLoading && (
          <div
            className="chart-empty"
            style={{ marginBottom: 16 }}
          >
            Seleccioná un proceso asistencial
            para ver su evolución clínica
            consolidada.
          </div>
        )}

      {programDetail && (
        <>
          <ClinicalComparisonWorkspace
            detail={programDetail}
            onChanged={onProgramChanged}
          />
          <PremiumAnatomicalMapWorkspace
            detail={programDetail}
            onChanged={onProgramChanged}
          />
        </>
      )}

      <div className="kpi-cards" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        {stats.map((s) => (
          <div className="kpi-card" key={s.label}>
            <div className="muted">{s.label}</div>
            <div className="big">{s.value}</div>
          </div>
        ))}
      </div>

      <h3 style={{ fontSize: 14, marginTop: 20 }}>Evolución antropométrica</h3>
      {sortedMeasurements.length === 0 ? (
        <p className="chart-empty">
          Sin mediciones antropométricas cargadas todavía — se agregan desde la pestaña "Historia longitudinal".
        </p>
      ) : (
        <table className="task-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Peso (kg)</th>
              <th>IMC</th>
              <th>Cintura (cm)</th>
              <th>Cadera (cm)</th>
              <th>% grasa</th>
              <th>Músculo (kg)</th>
              <th>Contexto</th>
              <th>Origen</th>
            </tr>
          </thead>
          <tbody>
            {sortedMeasurements.map((m) => (
              <tr key={m.id}>
                <td>{formatDateTime(m.measured_at)}</td>
                <td>{m.weight_kg ?? "—"}</td>
                <td>{m.bmi != null ? Number(m.bmi).toFixed(1) : "—"}</td>
                <td>{m.waist_cm ?? "—"}</td>
                <td>{m.hip_cm ?? "—"}</td>
                <td>{m.body_fat_pct ?? "—"}</td>
                <td>
                  {m.muscle_mass_kg ?? "—"}
                </td>
                <td>
                  {m.measurement_context ||
                    "FOLLOW_UP"}
                </td>
                <td>
                  {m.source_type ||
                    "MANUAL"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="chart-empty" style={{ marginTop: 16 }}>
        Comparativas recalculadas y snapshots agregados (`clinical_comparison_snapshots`) quedan pendientes de un job
        de recomputación — todavía no forman parte de este checkpoint. Lo mostrado arriba es la fuente real, medición
        por medición.
      </p>
    </div>
  );
}

type AnatomicalCanvasMarker = {
  id?: Id;
  marker_key: string;
  marker_type: string;
  geometry_type: string;
  normalized_geometry:
    Record<string, unknown>;
  label?: string | null;
};

function AnatomicalMapCanvas({
  markers,
  onPoint,
}: {
  markers: AnatomicalCanvasMarker[];
  onPoint?: (
    point: {
      x: number;
      y: number;
    }
  ) => void;
}) {
  function geometryPoints(
    marker: AnatomicalCanvasMarker
  ) {
    const geometry =
      marker.normalized_geometry;

    const rawPoints =
      Array.isArray(geometry.points)
        ? geometry.points
        : [];

    return rawPoints
      .filter(
        (point): point is {
          x: number;
          y: number;
        } =>
          typeof point === "object" &&
          point !== null &&
          typeof (
            point as {
              x?: unknown;
            }
          ).x === "number" &&
          typeof (
            point as {
              y?: unknown;
            }
          ).y === "number"
      )
      .map((point) => ({
        x: point.x * 300,
        y: point.y * 500,
      }));
  }

  function colorFor(markerType: string) {
    if (markerType === "TREATMENT") {
      return "#b56f58";
    }

    if (markerType === "DIAGNOSIS") {
      return "#8f3d4c";
    }

    if (markerType === "PROBLEM") {
      return "#d59a45";
    }

    return "#617b78";
  }

  return (
    <svg
      viewBox="0 0 300 500"
      role="img"
      aria-label="Mapa anatómico corporal"
      onClick={(event: any) => {
        if (!onPoint) return;

        const bounds =
          event.currentTarget
            .getBoundingClientRect();

        const x =
          (
            event.clientX -
            bounds.left
          ) /
          bounds.width;

        const y =
          (
            event.clientY -
            bounds.top
          ) /
          bounds.height;

        onPoint({
          x: Number(
            Math.max(
              0,
              Math.min(1, x)
            ).toFixed(4)
          ),
          y: Number(
            Math.max(
              0,
              Math.min(1, y)
            ).toFixed(4)
          ),
        });
      }}
      style={{
        width: "100%",
        maxHeight: 520,
        background:
          "linear-gradient(180deg, #fbfaf8 0%, #f2eee8 100%)",
        border:
          "1px solid rgba(97, 78, 67, 0.14)",
        borderRadius: 16,
        cursor: onPoint
          ? "crosshair"
          : "default",
      }}
    >
      <g
        fill="#e8dfd5"
        stroke="#9f8d7d"
        strokeWidth="2"
      >
        <circle
          cx="150"
          cy="55"
          r="34"
        />
        <path
          d="
            M118 92
            C100 112 96 155 92 205
            L72 318
            C69 332 79 337 87 324
            L112 222
            L116 315
            L101 466
            C100 481 115 486 121 470
            L150 337
            L179 470
            C185 486 200 481 199 466
            L184 315
            L188 222
            L213 324
            C221 337 231 332 228 318
            L208 205
            C204 155 200 112 182 92
            C164 102 136 102 118 92
            Z
          "
        />
      </g>

      <line
        x1="150"
        y1="95"
        x2="150"
        y2="430"
        stroke="rgba(97,78,67,.18)"
        strokeDasharray="4 5"
      />

      {markers.map((marker) => {
        const color =
          colorFor(
            marker.marker_type
          );

        const geometry =
          marker.normalized_geometry;

        if (
          marker.geometry_type ===
            "POINT" &&
          typeof geometry.x ===
            "number" &&
          typeof geometry.y ===
            "number"
        ) {
          return (
            <g key={marker.marker_key}>
              <circle
                cx={geometry.x * 300}
                cy={geometry.y * 500}
                r="10"
                fill={color}
                fillOpacity=".88"
                stroke="white"
                strokeWidth="3"
              />
              <circle
                cx={geometry.x * 300}
                cy={geometry.y * 500}
                r="16"
                fill="none"
                stroke={color}
                strokeOpacity=".35"
                strokeWidth="2"
              />
            </g>
          );
        }

        const points =
          geometryPoints(marker);

        if (
          points.length >= 2 &&
          marker.geometry_type ===
            "LINE"
        ) {
          return (
            <polyline
              key={marker.marker_key}
              points={points
                .map(
                  (point) =>
                    `${point.x},${point.y}`
                )
                .join(" ")}
              fill="none"
              stroke={color}
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        }

        if (points.length >= 3) {
          return (
            <polygon
              key={marker.marker_key}
              points={points
                .map(
                  (point) =>
                    `${point.x},${point.y}`
                )
                .join(" ")}
              fill={color}
              fillOpacity=".28"
              stroke={color}
              strokeWidth="3"
            />
          );
        }

        return null;
      })}
    </svg>
  );
}

// Visor autenticado de una fotografía clínica privada.
function PrivateClinicalImage({
  contactId,
  file,
}: {
  contactId: Id;
  file: PatientFile;
}) {
  const [source, setSource] =
    useState("");
  const [loading, setLoading] =
    useState(true);
  const [failed, setFailed] =
    useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    setLoading(true);
    setFailed(false);
    setSource("");

    api.downloadPrivateClinicalFile(
      contactId,
      file.id
    )
      .then((downloaded) => {
        if (!active) return;

        objectUrl =
          URL.createObjectURL(
            downloaded.blob
          );

        setSource(objectUrl);
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;

      if (objectUrl) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [contactId, file.id]);

  if (loading) {
    return (
      <div
        className="chart-empty"
        style={{ marginTop: 8 }}
      >
        Cargando vista privada…
      </div>
    );
  }

  if (failed || !source) {
    return (
      <div
        className="chart-empty"
        style={{ marginTop: 8 }}
      >
        No se pudo mostrar la fotografía.
      </div>
    );
  }

  return (
    <img
      src={source}
      alt={
        file.label ||
        file.original_filename ||
        "Fotografía clínica"
      }
      style={{
        width: "100%",
        aspectRatio: "4 / 3",
        objectFit: "contain",
        background: "#f6f4f0",
        borderRadius: 10,
        marginTop: 8,
      }}
    />
  );
}

function ClinicalComparisonCard({
  detail,
  comparison,
  signing,
  onSign,
}: {
  key?: Id;
  detail: ClinicalCareProgramDetailResponse;
  comparison:
    ClinicalCareProgramDetailResponse[
      "comparisons"
    ][number];
  signing: boolean;
  onSign: () => void;
}) {
  const baselineItem =
    comparison.items.find(
      (item) =>
        item.item_role === "BASELINE" &&
        item.patient_file_id != null
    );

  const comparatorItem =
    comparison.items.find(
      (item) =>
        item.item_role ===
          "COMPARATOR" &&
        item.patient_file_id != null
    );

  const baselineFile =
    detail.files.find(
      (file) =>
        String(file.id) ===
        String(
          baselineItem?.patient_file_id
        )
    );

  const comparatorFile =
    detail.files.find(
      (file) =>
        String(file.id) ===
        String(
          comparatorItem
            ?.patient_file_id
        )
    );

  return (
    <div className="chart-encounter-card">
      <div className="chart-list-actions">
        <div>
          <strong>
            {comparison.title}
          </strong>
          <div className="meta">
            {comparison.comparison_type}
            {" · "}
            {comparison.items.length}
            {" fuentes · "}
            {comparison.status}
          </div>
        </div>

        {comparison.status !==
          "SIGNED" && (
          <button
            className="btn-primary"
            disabled={signing}
            onClick={onSign}
          >
            {signing
              ? "Firmando…"
              : "Firmar"}
          </button>
        )}
      </div>

      {baselineFile &&
        comparatorFile && (
          <div
            className="clinical-comparison-grid"
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",
              gap: 12,
              marginTop: 12,
            }}
          >
            <div>
              <div className="meta">
                ANTES · {
                  formatDateTime(
                    baselineFile
                      .captured_at ||
                    baselineFile
                      .created_at
                  )
                }
              </div>
              <PrivateClinicalImage
                contactId={
                  detail.program
                    .contact_id
                }
                file={baselineFile}
              />
            </div>

            <div>
              <div className="meta">
                DESPUÉS · {
                  formatDateTime(
                    comparatorFile
                      .captured_at ||
                    comparatorFile
                      .created_at
                  )
                }
              </div>
              <PrivateClinicalImage
                contactId={
                  detail.program
                    .contact_id
                }
                file={comparatorFile}
              />
            </div>
          </div>
        )}

      {comparison.clinical_question && (
        <div
          className="meta"
          style={{ marginTop: 10 }}
        >
          Pregunta clínica:{" "}
          {comparison.clinical_question}
        </div>
      )}

      {comparison.conclusion && (
        <p style={{ marginBottom: 0 }}>
          <strong>Conclusión:</strong>{" "}
          {comparison.conclusion}
        </p>
      )}

      {comparison.status ===
        "SIGNED" &&
        comparison.signed_at && (
          <div
            className="meta"
            style={{ marginTop: 8 }}
          >
            Firmada el{" "}
            {formatDateTime(
              comparison.signed_at
            )}
          </div>
        )}
    </div>
  );
}

function ClinicalAnatomicalMapWorkspace({
  detail,
  onChanged,
}: {
  detail: ClinicalCareProgramDetailResponse;
  onChanged: () => void | Promise<void>;
}) {
  const { show } = useToast();
  const [creating, setCreating] =
    useState(false);
  const [submitting, setSubmitting] =
    useState(false);
  const [selectedPoint, setSelectedPoint] =
    useState<{
      x: number;
      y: number;
    } | null>(null);
  const [sessionId, setSessionId] =
    useState("");
  const [purpose, setPurpose] =
    useState<
      "ASSESSMENT" |
      "DIAGNOSIS" |
      "TREATMENT" |
      "FOLLOW_UP"
    >("TREATMENT");
  const [markerType, setMarkerType] =
    useState<
      "PROBLEM" |
      "DIAGNOSIS" |
      "TREATMENT" |
      "OBSERVATION"
    >("TREATMENT");
  const [zoneCode, setZoneCode] =
    useState("");
  const [markerLabel, setMarkerLabel] =
    useState("");
  const [diagnosisText, setDiagnosisText] =
    useState("");
  const [plannedTreatment, setPlannedTreatment] =
    useState("");
  const [dose, setDose] =
    useState("");
  const [doseUnit, setDoseUnit] =
    useState("");
  const [notes, setNotes] =
    useState("");
  const [signingId, setSigningId] =
    useState<Id | null>(null);

  async function createMap() {
    if (!selectedPoint) {
      show(
        "Marcá una ubicación sobre la silueta corporal.",
        "error"
      );
      return;
    }

    if (!markerLabel.trim()) {
      show(
        "Ingresá una descripción para la marca.",
        "error"
      );
      return;
    }

    setSubmitting(true);

    try {
      await api.createClinicalAnatomicalMap(
        detail.program.id,
        {
          session_id:
            sessionId || undefined,
          map_type: "BODY",
          view_code: "FRONT",
          purpose,
          template_code:
            "BODY_FRONT_V1",
          template_version: 1,
          notes:
            notes.trim() || undefined,
          patient_visibility:
            "INTERNAL",
          markers: [
            {
              marker_key:
                `body-front-${Date.now()}`,
              marker_type:
                markerType,
              geometry_type: "POINT",
              normalized_geometry:
                selectedPoint,
              zone_code:
                zoneCode.trim() ||
                undefined,
              laterality:
                selectedPoint.x < 0.46
                  ? "RIGHT"
                  : selectedPoint.x > 0.54
                    ? "LEFT"
                    : "MIDLINE",
              label:
                markerLabel.trim(),
              diagnosis_text:
                diagnosisText.trim() ||
                undefined,
              planned_treatment:
                plannedTreatment.trim()
                  ? {
                      treatment:
                        plannedTreatment.trim(),
                      dose:
                        dose
                          ? Number(dose)
                          : null,
                      unit:
                        doseUnit.trim() ||
                        null,
                    }
                  : {},
              clinical_details: {
                source:
                  "interactive_body_map",
              },
            },
          ],
        }
      );

      show(
        "Mapa anatómico creado en borrador.",
        "success"
      );

      setSelectedPoint(null);
      setSessionId("");
      setPurpose("TREATMENT");
      setMarkerType("TREATMENT");
      setZoneCode("");
      setMarkerLabel("");
      setDiagnosisText("");
      setPlannedTreatment("");
      setDose("");
      setDoseUnit("");
      setNotes("");
      setCreating(false);

      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo crear el mapa anatómico.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function signMap(mapId: Id) {
    setSigningId(mapId);

    try {
      await api.signClinicalAnatomicalMap(
        detail.program.id,
        mapId
      );

      show(
        "Mapa anatómico firmado.",
        "success"
      );

      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo firmar el mapa.",
        "error"
      );
    } finally {
      setSigningId(null);
    }
  }

  return (
    <div
      className="chart-encounter-card"
      style={{ marginBottom: 18 }}
    >
      <div className="chart-list-actions">
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 15,
            }}
          >
            Mapas anatómicos
          </h3>
          <div className="meta">
            Zonas, diagnósticos y tratamientos
            ubicados sobre anatomía normalizada.
          </div>
        </div>

        {!creating && (
          <button
            className="btn-primary"
            onClick={() =>
              setCreating(true)
            }
          >
            + Nuevo mapa
          </button>
        )}
      </div>

      {creating && (
        <div
          className="chart-encounter-card"
          style={{ marginTop: 12 }}
        >
          <div className="clinical-map-editor">
            <div>
              <div
                className="meta"
                style={{ marginBottom: 8 }}
              >
                Tocá la zona corporal que
                querés registrar.
              </div>

              <AnatomicalMapCanvas
                markers={
                  selectedPoint
                    ? [
                        {
                          marker_key:
                            "draft-marker",
                          marker_type:
                            markerType,
                          geometry_type:
                            "POINT",
                          normalized_geometry:
                            selectedPoint,
                          label:
                            markerLabel ||
                            "Nueva marca",
                        },
                      ]
                    : []
                }
                onPoint={setSelectedPoint}
              />

              {selectedPoint && (
                <div
                  className="meta"
                  style={{ marginTop: 7 }}
                >
                  Posición normalizada:{" "}
                  {selectedPoint.x},{" "}
                  {selectedPoint.y}
                </div>
              )}
            </div>

            <div className="chart-form">
              <div className="chart-form-row">
                <select
                  value={purpose}
                  onChange={(event: any) =>
                    setPurpose(
                      event.target.value
                    )
                  }
                >
                  <option value="ASSESSMENT">
                    Evaluación
                  </option>
                  <option value="DIAGNOSIS">
                    Diagnóstico
                  </option>
                  <option value="TREATMENT">
                    Tratamiento
                  </option>
                  <option value="FOLLOW_UP">
                    Seguimiento
                  </option>
                </select>

                <select
                  value={markerType}
                  onChange={(event: any) =>
                    setMarkerType(
                      event.target.value
                    )
                  }
                >
                  <option value="PROBLEM">
                    Problema
                  </option>
                  <option value="DIAGNOSIS">
                    Diagnóstico
                  </option>
                  <option value="TREATMENT">
                    Tratamiento
                  </option>
                  <option value="OBSERVATION">
                    Observación
                  </option>
                </select>
              </div>

              <select
                value={sessionId}
                onChange={(event: any) =>
                  setSessionId(
                    event.target.value
                  )
                }
              >
                <option value="">
                  Sin sesión específica
                </option>
                {detail.sessions.map(
                  (session) => (
                    <option
                      key={session.id}
                      value={String(
                        session.id
                      )}
                    >
                      Sesión {
                        session.session_number
                      } · {
                        session.session_type
                      }
                    </option>
                  )
                )}
              </select>

              <input
                placeholder="Zona anatómica (ej. abdomen central)"
                value={zoneCode}
                onChange={(event: any) =>
                  setZoneCode(
                    event.target.value
                  )
                }
              />

              <input
                placeholder="Descripción de la marca"
                value={markerLabel}
                onChange={(event: any) =>
                  setMarkerLabel(
                    event.target.value
                  )
                }
              />

              <textarea
                rows={2}
                placeholder="Diagnóstico o hallazgo clínico"
                value={diagnosisText}
                onChange={(event: any) =>
                  setDiagnosisText(
                    event.target.value
                  )
                }
              />

              <input
                placeholder="Tratamiento planificado"
                value={plannedTreatment}
                onChange={(event: any) =>
                  setPlannedTreatment(
                    event.target.value
                  )
                }
              />

              <div className="chart-form-row">
                <input
                  type="number"
                  step="any"
                  placeholder="Dosis"
                  value={dose}
                  onChange={(event: any) =>
                    setDose(
                      event.target.value
                    )
                  }
                />
                <input
                  placeholder="Unidad (mg, ml, U)"
                  value={doseUnit}
                  onChange={(event: any) =>
                    setDoseUnit(
                      event.target.value
                    )
                  }
                />
              </div>

              <textarea
                rows={2}
                placeholder="Notas generales del mapa"
                value={notes}
                onChange={(event: any) =>
                  setNotes(
                    event.target.value
                  )
                }
              />

              <div className="row">
                <button
                  className="btn-primary"
                  disabled={
                    submitting ||
                    !selectedPoint
                  }
                  onClick={createMap}
                >
                  {submitting
                    ? "Guardando…"
                    : "Crear mapa en borrador"}
                </button>
                <button
                  className="btn-ghost"
                  disabled={submitting}
                  onClick={() => {
                    setCreating(false);
                    setSelectedPoint(null);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detail.anatomical_maps.length ===
      0 ? (
        <div
          className="chart-empty"
          style={{ marginTop: 10 }}
        >
          Todavía no se registró un mapa
          anatómico para este proceso.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 14,
            marginTop: 12,
          }}
        >
          {detail.anatomical_maps.map(
            (anatomicalMap) => (
              <div
                key={anatomicalMap.id}
                className="chart-encounter-card"
              >
                <div className="chart-list-actions">
                  <div>
                    <strong>
                      {
                        anatomicalMap
                          .map_type
                      }
                      {" · "}
                      {
                        anatomicalMap
                          .view_code
                      }
                    </strong>
                    <div className="meta">
                      {
                        anatomicalMap
                          .purpose
                      }
                      {" · "}
                      {
                        anatomicalMap
                          .status
                      }
                    </div>
                  </div>

                  {anatomicalMap.status !==
                    "SIGNED" && (
                    <button
                      className="btn-primary"
                      disabled={
                        signingId ===
                        anatomicalMap.id
                      }
                      onClick={() =>
                        signMap(
                          anatomicalMap.id
                        )
                      }
                    >
                      {signingId ===
                      anatomicalMap.id
                        ? "Firmando…"
                        : "Firmar"}
                    </button>
                  )}
                </div>

                <div
                  style={{
                    maxWidth: 300,
                    margin:
                      "12px auto 0",
                  }}
                >
                  <AnatomicalMapCanvas
                    markers={
                      anatomicalMap.markers
                    }
                  />
                </div>

                {anatomicalMap.markers
                  .length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gap: 5,
                      marginTop: 10,
                    }}
                  >
                    {anatomicalMap.markers.map(
                      (marker) => (
                        <div
                          className="meta"
                          key={marker.id}
                        >
                          ●{" "}
                          {marker.label ||
                            marker.zone_code ||
                            marker.marker_key}
                          {" · "}
                          {
                            marker.marker_type
                          }
                        </div>
                      )
                    )}
                  </div>
                )}

                {anatomicalMap.notes && (
                  <p
                    style={{
                      marginBottom: 0,
                    }}
                  >
                    {anatomicalMap.notes}
                  </p>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Comparador clínico curado. Cada documento conserva referencias a sus
// fuentes reales; no copia ni recalcula evidencia clínica.
// ---------------------------------------------------------------------
function ClinicalComparisonWorkspace({
  detail,
  onChanged,
}: {
  detail: ClinicalCareProgramDetailResponse;
  onChanged: () => void | Promise<void>;
}) {
  const { show } = useToast();
  const [creating, setCreating] =
    useState(false);
  const [submitting, setSubmitting] =
    useState(false);
  const [title, setTitle] =
    useState("");
  const [clinicalQuestion, setClinicalQuestion] =
    useState("");
  const [conclusion, setConclusion] =
    useState("");
  const [baselineFileId, setBaselineFileId] =
    useState("");
  const [
    comparatorFileId,
    setComparatorFileId,
  ] = useState("");
  const [signingId, setSigningId] =
    useState<Id | null>(null);

  const comparableFiles =
    detail.files.filter(
      (file) =>
        file.file_type === "PHOTO" &&
        file.comparison_ready === true &&
        file.managed_storage === true &&
        file.content_status ===
          "AVAILABLE"
    );

  const baselineFile =
    comparableFiles.find(
      (file) =>
        String(file.id) ===
        baselineFileId
    );

  const comparatorFile =
    comparableFiles.find(
      (file) =>
        String(file.id) ===
        comparatorFileId
    );

  async function createComparison() {
    if (
      !title.trim() ||
      !conclusion.trim()
    ) {
      show(
        "Ingresá un título y una conclusión clínica.",
        "error"
      );
      return;
    }

    if (
      !baselineFileId ||
      !comparatorFileId
    ) {
      show(
        "Seleccioná una fotografía inicial y una comparadora.",
        "error"
      );
      return;
    }

    if (
      baselineFileId ===
      comparatorFileId
    ) {
      show(
        "Las fotografías deben ser diferentes.",
        "error"
      );
      return;
    }

    const baseline =
      comparableFiles.find(
        (file) =>
          String(file.id) ===
          baselineFileId
      );

    const comparator =
      comparableFiles.find(
        (file) =>
          String(file.id) ===
          comparatorFileId
      );

    setSubmitting(true);

    try {
      await api.createClinicalComparison(
        detail.program.id,
        {
          comparison_type: "PHOTO",
          title: title.trim(),
          clinical_question:
            clinicalQuestion.trim() ||
            undefined,
          baseline_at:
            baseline?.captured_at ||
            baseline?.created_at,
          comparison_at:
            comparator?.captured_at ||
            comparator?.created_at,
          conclusion:
            conclusion.trim(),
          patient_visibility:
            "INTERNAL",
          items: [
            {
              item_role: "BASELINE",
              patient_file_id:
                baselineFileId,
              position: 0,
            },
            {
              item_role:
                "COMPARATOR",
              patient_file_id:
                comparatorFileId,
              position: 0,
            },
          ],
        }
      );

      show(
        "Comparación clínica creada en borrador.",
        "success"
      );

      setTitle("");
      setClinicalQuestion("");
      setConclusion("");
      setBaselineFileId("");
      setComparatorFileId("");
      setCreating(false);

      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo crear la comparación.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function signComparison(
    comparisonId: Id
  ) {
    setSigningId(comparisonId);

    try {
      await api.signClinicalComparison(
        detail.program.id,
        comparisonId
      );

      show(
        "Comparación clínica firmada.",
        "success"
      );

      await onChanged();
    } catch (error) {
      show(
        error instanceof ApiClientError
          ? error.message
          : "No se pudo firmar la comparación.",
        "error"
      );
    } finally {
      setSigningId(null);
    }
  }

  return (
    <div
      className="chart-encounter-card"
      style={{ marginBottom: 18 }}
    >
      <div className="chart-list-actions">
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 15,
            }}
          >
            Comparaciones clínicas
          </h3>
          <div className="meta">
            Antes / después con fuentes
            trazables y conclusión profesional.
          </div>
        </div>

        {!creating && (
          <button
            className="btn-primary"
            disabled={
              comparableFiles.length < 2
            }
            onClick={() =>
              setCreating(true)
            }
          >
            + Nueva comparación
          </button>
        )}
      </div>

      {comparableFiles.length < 2 &&
        !creating && (
          <div
            className="chart-notice"
            style={{ marginTop: 10 }}
          >
            Para crear una comparativa se
            necesitan al menos dos fotografías
            marcadas como comparables en la
            pestaña Archivos.
          </div>
        )}

      {creating && (
        <div
          className="chart-encounter-card"
          style={{ marginTop: 12 }}
        >
          <div className="chart-form">
            <input
              placeholder="Título de la comparación"
              value={title}
              onChange={(event: any) =>
                setTitle(event.target.value)
              }
            />

            <input
              placeholder="Pregunta clínica (opcional)"
              value={clinicalQuestion}
              onChange={(event: any) =>
                setClinicalQuestion(
                  event.target.value
                )
              }
            />

            <div
              className="clinical-comparison-grid"
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(2, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <div
                className="chart-encounter-card"
              >
                <strong>Antes</strong>
                <select
                  value={baselineFileId}
                  onChange={(event: any) =>
                    setBaselineFileId(
                      event.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    marginTop: 8,
                  }}
                >
                  <option value="">
                    Seleccionar fotografía…
                  </option>
                  {comparableFiles.map(
                    (file) => (
                      <option
                        key={file.id}
                        value={String(file.id)}
                      >
                        {file.label ||
                          file.original_filename ||
                          `Archivo ${file.id}`}
                        {" · "}
                        {formatDateTime(
                          file.captured_at ||
                          file.created_at
                        )}
                      </option>
                    )
                  )}
                </select>

                {baselineFile && (
                  <PrivateClinicalImage
                    contactId={
                      detail.program
                        .contact_id
                    }
                    file={baselineFile}
                  />
                )}
              </div>

              <div
                className="chart-encounter-card"
              >
                <strong>Después</strong>
                <select
                  value={comparatorFileId}
                  onChange={(event: any) =>
                    setComparatorFileId(
                      event.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    marginTop: 8,
                  }}
                >
                  <option value="">
                    Seleccionar fotografía…
                  </option>
                  {comparableFiles.map(
                    (file) => (
                      <option
                        key={file.id}
                        value={String(file.id)}
                      >
                        {file.label ||
                          file.original_filename ||
                          `Archivo ${file.id}`}
                        {" · "}
                        {formatDateTime(
                          file.captured_at ||
                          file.created_at
                        )}
                      </option>
                    )
                  )}
                </select>

                {comparatorFile && (
                  <PrivateClinicalImage
                    contactId={
                      detail.program
                        .contact_id
                    }
                    file={comparatorFile}
                  />
                )}
              </div>
            </div>

            <textarea
              rows={3}
              placeholder="Conclusión profesional: cambios observados, interpretación y límites de la comparación"
              value={conclusion}
              onChange={(event: any) =>
                setConclusion(
                  event.target.value
                )
              }
            />

            <div className="row">
              <button
                className="btn-primary"
                disabled={submitting}
                onClick={createComparison}
              >
                {submitting
                  ? "Creando…"
                  : "Crear borrador"}
              </button>
              <button
                className="btn-ghost"
                disabled={submitting}
                onClick={() =>
                  setCreating(false)
                }
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {detail.comparisons.length === 0 ? (
        <div
          className="chart-empty"
          style={{ marginTop: 10 }}
        >
          Todavía no se creó una comparación
          clínica para este proceso.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: 10,
            marginTop: 12,
          }}
        >
          {detail.comparisons.map(
            (comparison) => (
              <ClinicalComparisonCard
                key={comparison.id}
                detail={detail}
                comparison={comparison}
                signing={
                  signingId ===
                  comparison.id
                }
                onSign={() =>
                  signComparison(
                    comparison.id
                  )
                }
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

// =======================================================================
// Historia longitudinal — Fase 1B frontend (checkpoint posterior al
// backend, 2026-08-23). Expone funcionalmente las 7 entidades de
// Migración 14 que hasta ahora sólo existían como schema + endpoints.
//
// Política de datos legacy (fijada en la revisión de Migración 14, ver
// FASE1B_RECONCILIACION.md §9): el texto libre de
// patient_medical_profiles.allergies/current_medications se muestra sólo
// mientras el paciente no tenga ninguna fila estructurada de esa
// categoría — evaluado independientemente por categoría, sin migración
// manual. En cuanto exista al menos una fila estructurada, se deja de
// mostrar el texto legacy correspondiente. Implementado en
// AllergiesSection/MedicationsSection abajo.
// =======================================================================
function LongitudinalPanel({
  contactId,
  medicalProfile,
  professionals,
  defaultProfessionalId,
  myProfessionalId,
  problems,
  allergies,
  medications,
  studies,
  measurements,
  programId,
  sessions,
  protocolTemplates,
  protocolRecords,
  implants,
  onChanged,
}: {
  contactId: Id;
  medicalProfile: PatientMedicalProfile | null;
  professionals: Professional[];
  defaultProfessionalId: string;
  myProfessionalId: number | null;
  problems: ClinicalProblem[];
  allergies: Allergy[];
  medications: ActiveMedication[];
  studies: DiagnosticStudy[];
  measurements: AnthropometricMeasurement[];
  programId?: Id;
  sessions: import("../types")
    .ClinicalCareSession[];
  protocolTemplates: TreatmentProtocolTemplate[];
  protocolRecords: TreatmentProtocolRecord[];
  implants: ImplantDeviceRecord[];
  onChanged: () => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <PregnancySection contactId={contactId} medicalProfile={medicalProfile} onChanged={onChanged} />
      <ProblemsSection contactId={contactId} problems={problems} onChanged={onChanged} />
      <AllergiesSection contactId={contactId} allergies={allergies} legacyText={medicalProfile?.allergies} onChanged={onChanged} />
      <MedicationsSection contactId={contactId} medications={medications} legacyText={medicalProfile?.current_medications} onChanged={onChanged} />
      <StudiesSection contactId={contactId} studies={studies} professionals={professionals} defaultProfessionalId={defaultProfessionalId} onChanged={onChanged} />
      <MeasurementsSection
        contactId={contactId}
        measurements={measurements}
        programId={programId}
        sessions={sessions}
        onChanged={onChanged}
      />
      <ProtocolsSection
        contactId={contactId}
        templates={protocolTemplates}
        records={protocolRecords}
        professionals={professionals}
        defaultProfessionalId={defaultProfessionalId}
        myProfessionalId={myProfessionalId}
        onChanged={onChanged}
      />
      <ImplantsSection contactId={contactId} implants={implants} onChanged={onChanged} />
    </div>
  );
}

function SectionHeader({ title, open, onToggle }: { title: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="chart-list-actions">
      <h3 style={{ margin: 0, fontSize: 14 }}>{title}</h3>
      <button className="btn-ghost" onClick={onToggle}>
        {open ? "Cancelar" : "+ Agregar"}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------
// Estado gestacional — única fuente de PREGNANCY_RELEVANT (ver
// lib/clinicalAlerts.js en el backend). No hay tabla estructurada propia
// todavía: pregnancy_status sigue viviendo en patient_medical_profiles,
// pero ahora tiene un endpoint de escritura real (antes no existía
// ninguno en todo el código).
// -----------------------------------------------------------------------
function PregnancySection({
  contactId,
  medicalProfile,
  onChanged,
}: {
  contactId: Id;
  medicalProfile: PatientMedicalProfile | null;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [value, setValue] = useState(medicalProfile?.pregnancy_status || "N/A");
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(medicalProfile?.pregnancy_status || "N/A"), [medicalProfile?.pregnancy_status]);

  async function save() {
    setSaving(true);
    try {
      await api.updatePatientPregnancyStatus(contactId, value);
      show("Estado gestacional actualizado.", "success");
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo actualizar.", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h3 style={{ margin: 0, fontSize: 14, marginBottom: 8 }}>Estado gestacional</h3>
      <div className="chart-form-row">
        <select value={value} onChange={(e: any) => setValue(e.target.value)}>
          {PREGNANCY_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <button className="btn-primary" disabled={saving} onClick={save}>
          Guardar
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Problemas clínicos longitudinales
// -----------------------------------------------------------------------
function ProblemsSection({ contactId, problems, onChanged }: { contactId: Id; problems: ClinicalProblem[]; onChanged: () => void }) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [severity, setSeverity] = useState("");
  const [onsetDate, setOnsetDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState<Id | null>(null);

  async function submit() {
    if (!name.trim()) return show("Ingresá el nombre del problema.", "error");
    setSubmitting(true);
    try {
      await api.createClinicalProblem({ contact_id: contactId, problem_name: name, severity: severity || undefined, onset_date: onsetDate || undefined });
      show("Problema clínico registrado.", "success");
      setName("");
      setSeverity("");
      setOnsetDate("");
      setOpen(false);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo registrar.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function changeStatus(id: Id, status: string) {
    setUpdatingId(id);
    try {
      await api.updateClinicalProblem(id, { status: status as any });
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo actualizar.", "error");
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div>
      <SectionHeader title="Problemas clínicos" open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <input placeholder="Problema / diagnóstico" value={name} onChange={(e: any) => setName(e.target.value)} />
            <div className="chart-form-row">
              <input placeholder="Severidad (opcional)" value={severity} onChange={(e: any) => setSeverity(e.target.value)} />
              <input type="date" value={onsetDate} onChange={(e: any) => setOnsetDate(e.target.value)} />
            </div>
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {problems.length === 0 && !open && <div className="chart-empty">Sin problemas clínicos registrados.</div>}
      {problems.map((p) => (
        <div className="chart-encounter-card" key={p.id}>
          <div className="head">
            <span className={p.status === "ACTIVE" ? "pill pill-warn" : p.status === "RESOLVED" ? "pill pill-ok" : "pill"}>{p.status}</span>
            <span className="meta">{p.problem_name}{p.severity ? ` · ${p.severity}` : ""}</span>
          </div>
          {p.notes && <p className="meta">{p.notes}</p>}
          {p.status === "ACTIVE" && (
            <div className="row">
              {PROBLEM_STATUSES.filter((s) => s !== "ACTIVE").map((s) => (
                <button key={s} className="btn-ghost" disabled={updatingId === p.id} onClick={() => changeStatus(p.id, s)}>
                  Marcar {s}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Alergias — fuente estructurada nueva; el texto legacy de
// patient_medical_profiles.allergies se muestra sólo mientras esta lista
// esté vacía (política de la revisión de Migración 14).
// -----------------------------------------------------------------------
function AllergiesSection({
  contactId,
  allergies,
  legacyText,
  onChanged,
}: {
  contactId: Id;
  allergies: Allergy[];
  legacyText?: string | null;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [substance, setSubstance] = useState("");
  const [reaction, setReaction] = useState("");
  const [severity, setSeverity] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!substance.trim()) return show("Ingresá la sustancia.", "error");
    setSubmitting(true);
    try {
      await api.createAllergy({ contact_id: contactId, substance, reaction: reaction || undefined, severity: severity || undefined });
      show("Alergia registrada.", "success");
      setSubstance("");
      setReaction("");
      setSeverity("");
      setOpen(false);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo registrar.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <SectionHeader title="Alergias" open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <input placeholder="Sustancia" value={substance} onChange={(e: any) => setSubstance(e.target.value)} />
            <div className="chart-form-row">
              <input placeholder="Reacción (opcional)" value={reaction} onChange={(e: any) => setReaction(e.target.value)} />
              <select value={severity} onChange={(e: any) => setSeverity(e.target.value)}>
                <option value="">Severidad...</option>
                <option value="MILD">Leve</option>
                <option value="MODERATE">Moderada</option>
                <option value="SEVERE">Severa</option>
              </select>
            </div>
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {allergies.length === 0 && !open && (
        legacyText ? (
          <div className="chart-encounter-card">
            <span className="meta">{legacyText} (registro anterior, sin estructurar)</span>
          </div>
        ) : (
          <div className="chart-empty">Sin alergias registradas.</div>
        )
      )}
      {allergies.map((a) => (
        <div className="chart-encounter-card" key={a.id}>
          <div className="head">
            <span className="pill pill-bad">{a.substance}</span>
            {a.severity && <span className="meta">{a.severity}</span>}
          </div>
          {a.reaction && <p className="meta">{a.reaction}</p>}
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Medicación activa — mismo criterio de legacy que alergias.
// -----------------------------------------------------------------------
function MedicationsSection({
  contactId,
  medications,
  legacyText,
  onChanged,
}: {
  contactId: Id;
  medications: ActiveMedication[];
  legacyText?: string | null;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [drugName, setDrugName] = useState("");
  const [dose, setDose] = useState("");
  const [frequency, setFrequency] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState<Id | null>(null);

  async function submit() {
    if (!drugName.trim()) return show("Ingresá el medicamento.", "error");
    setSubmitting(true);
    try {
      await api.createActiveMedication({ contact_id: contactId, drug_name: drugName, dose: dose || undefined, frequency: frequency || undefined });
      show("Medicación registrada.", "success");
      setDrugName("");
      setDose("");
      setFrequency("");
      setOpen(false);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo registrar.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function discontinue(id: Id) {
    setUpdatingId(id);
    try {
      await api.updateActiveMedication(id, { status: "DISCONTINUED", ended_at: new Date().toISOString().slice(0, 10) });
      show("Medicación discontinuada.", "success");
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo actualizar.", "error");
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <div>
      <SectionHeader title="Medicación activa" open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <input placeholder="Medicamento" value={drugName} onChange={(e: any) => setDrugName(e.target.value)} />
            <div className="chart-form-row">
              <input placeholder="Dosis" value={dose} onChange={(e: any) => setDose(e.target.value)} />
              <input placeholder="Frecuencia" value={frequency} onChange={(e: any) => setFrequency(e.target.value)} />
            </div>
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {medications.length === 0 && !open && (
        legacyText ? (
          <div className="chart-encounter-card">
            <span className="meta">{legacyText} (registro anterior, sin estructurar)</span>
          </div>
        ) : (
          <div className="chart-empty">Sin medicación activa registrada.</div>
        )
      )}
      {medications.map((m) => (
        <div className="chart-encounter-card" key={m.id}>
          <div className="head">
            <span className={m.status === "ACTIVE" ? "pill pill-warn" : "pill"}>{m.status}</span>
            <span className="meta">
              {m.drug_name}
              {m.dose ? ` · ${m.dose}` : ""}
              {m.frequency ? ` · ${m.frequency}` : ""}
            </span>
          </div>
          {m.status === "ACTIVE" && (
            <button className="btn-ghost" disabled={updatingId === m.id} onClick={() => discontinue(m.id)}>
              Discontinuar
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Estudios diagnósticos + resultados
// -----------------------------------------------------------------------
function StudiesSection({
  contactId,
  studies,
  professionals,
  defaultProfessionalId,
  onChanged,
}: {
  contactId: Id;
  studies: DiagnosticStudy[];
  professionals: Professional[];
  defaultProfessionalId: string;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [studyType, setStudyType] = useState("LAB");
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState<Id | null>(null);
  const [resultsOpenFor, setResultsOpenFor] = useState<Id | null>(null);
  const [analyteName, setAnalyteName] = useState("");
  const [numericValue, setNumericValue] = useState("");
  const [flag, setFlag] = useState("NORMAL");
  const [savingResult, setSavingResult] = useState(false);

  async function submit() {
    if (!title.trim()) return show("Ingresá el título del estudio.", "error");
    setSubmitting(true);
    try {
      await api.createDiagnosticStudy({ contact_id: contactId, study_type: studyType, title });
      show("Estudio registrado.", "success");
      setTitle("");
      setOpen(false);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo registrar.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function changeStatus(id: Id, status: string) {
    setUpdatingId(id);
    try {
      await api.updateDiagnosticStudy(id, { status });
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo actualizar.", "error");
    } finally {
      setUpdatingId(null);
    }
  }

  async function submitResult(studyId: Id) {
    if (!analyteName.trim()) return show("Ingresá el nombre del analito.", "error");
    setSavingResult(true);
    try {
      await api.addStudyResults(studyId, [
        { analyte_name: analyteName, numeric_value: numericValue ? Number(numericValue) : undefined, flag: flag as any },
      ]);
      show("Resultado cargado.", "success");
      setAnalyteName("");
      setNumericValue("");
      setFlag("NORMAL");
      setResultsOpenFor(null);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo cargar el resultado.", "error");
    } finally {
      setSavingResult(false);
    }
  }

  return (
    <div>
      <SectionHeader title="Estudios diagnósticos" open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <div className="chart-form-row">
              <select value={studyType} onChange={(e: any) => setStudyType(e.target.value)}>
                {STUDY_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
              <input placeholder="Título del estudio" value={title} onChange={(e: any) => setTitle(e.target.value)} />
            </div>
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {studies.length === 0 && !open && <div className="chart-empty">Sin estudios diagnósticos registrados.</div>}
      {studies.map((s) => (
        <div className="chart-encounter-card" key={s.id}>
          <div className="head">
            <span className={s.abnormal ? "pill pill-bad" : s.status === "REVIEWED" || s.status === "COMPLETED" ? "pill pill-ok" : "pill pill-warn"}>{s.status}</span>
            <span className="meta">{s.title} · {STUDY_TYPES.find((t) => t.code === s.study_type)?.label || s.study_type}</span>
          </div>
          {s.result_values && s.result_values.length > 0 && (
            <ul>
              {s.result_values.map((v) => (
                <li key={v.id}>
                  {v.analyte_name}: {v.numeric_value ?? v.text_value ?? "—"} {v.unit || ""} {v.flag && v.flag !== "NORMAL" ? `(${v.flag})` : ""}
                </li>
              ))}
            </ul>
          )}
          <div className="row">
            {STUDY_STATUSES.filter((st) => st !== s.status).map((st) => (
              <button key={st} className="btn-ghost" disabled={updatingId === s.id} onClick={() => changeStatus(s.id, st)}>
                → {st}
              </button>
            ))}
            <button className="btn-ghost" onClick={() => setResultsOpenFor(resultsOpenFor === s.id ? null : s.id)}>
              + Resultado
            </button>
          </div>
          {resultsOpenFor === s.id && (
            <div className="chart-form" style={{ marginTop: 8 }}>
              <div className="chart-form-row">
                <input placeholder="Analito" value={analyteName} onChange={(e: any) => setAnalyteName(e.target.value)} />
                <input placeholder="Valor" value={numericValue} onChange={(e: any) => setNumericValue(e.target.value)} />
                <select value={flag} onChange={(e: any) => setFlag(e.target.value)}>
                  {RESULT_FLAGS.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>
              <div className="row">
                <button className="btn-primary" disabled={savingResult} onClick={() => submitResult(s.id)}>
                  Guardar resultado
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// Mediciones antropométricas — también alimentan la pestaña Comparativas.
// -----------------------------------------------------------------------
function MeasurementsSection({
  contactId,
  measurements,
  programId,
  sessions,
  onChanged,
}: {
  contactId: Id;
  measurements: AnthropometricMeasurement[];
  programId?: Id;
  sessions: import("../types")
    .ClinicalCareSession[];
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [waist, setWaist] = useState("");
  const [hip, setHip] = useState("");
  const [bodyFat, setBodyFat] = useState("");
  const [fatMass, setFatMass] = useState("");
  const [leanMass, setLeanMass] = useState("");
  const [muscleMass, setMuscleMass] = useState("");
  const [waterPct, setWaterPct] = useState("");
  const [visceralFat, setVisceralFat] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [measurementContext, setMeasurementContext] =
    useState<
      "BASELINE" |
      "PRE_SESSION" |
      "POST_SESSION" |
      "FOLLOW_UP"
    >("FOLLOW_UP");
  const [sourceType, setSourceType] =
    useState<
      "MANUAL" |
      "BIOIMPEDANCE" |
      "BODY_SCAN" |
      "OTHER"
    >("MANUAL");
  const [sourceDevice, setSourceDevice] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!weight && !height) return show("Ingresá al menos peso o altura.", "error");
    setSubmitting(true);
    try {
      await api.createAnthropometricMeasurement({
        contact_id: contactId,
        program_id: programId,
        session_id:
          sessionId || undefined,
        weight_kg:
          weight ? Number(weight) : undefined,
        height_cm:
          height ? Number(height) : undefined,
        waist_cm:
          waist ? Number(waist) : undefined,
        hip_cm:
          hip ? Number(hip) : undefined,
        body_fat_pct:
          bodyFat ? Number(bodyFat) : undefined,
        fat_mass_kg:
          fatMass ? Number(fatMass) : undefined,
        lean_mass_kg:
          leanMass ? Number(leanMass) : undefined,
        muscle_mass_kg:
          muscleMass
            ? Number(muscleMass)
            : undefined,
        water_pct:
          waterPct
            ? Number(waterPct)
            : undefined,
        visceral_fat:
          visceralFat
            ? Number(visceralFat)
            : undefined,
        measurement_context:
          measurementContext,
        source_type: sourceType,
        source_device:
          sourceDevice || undefined,
        notes: notes || undefined,
      });

      show(
        "Medición corporal registrada.",
        "success"
      );

      setWeight("");
      setHeight("");
      setWaist("");
      setHip("");
      setBodyFat("");
      setFatMass("");
      setLeanMass("");
      setMuscleMass("");
      setWaterPct("");
      setVisceralFat("");
      setSessionId("");
      setMeasurementContext("FOLLOW_UP");
      setSourceType("MANUAL");
      setSourceDevice("");
      setNotes("");
      setOpen(false);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo registrar.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  const latest = [...measurements].sort((a, b) => new Date(b.measured_at).getTime() - new Date(a.measured_at).getTime())[0];

  return (
    <div>
      <SectionHeader title="Antropometría" open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <div className="meta">
              Medidas generales
            </div>
            <div className="chart-form-row">
              <input
                type="number"
                step="any"
                placeholder="Peso (kg)"
                value={weight}
                onChange={(event: any) =>
                  setWeight(event.target.value)
                }
              />
              <input
                type="number"
                step="any"
                placeholder="Altura (cm)"
                value={height}
                onChange={(event: any) =>
                  setHeight(event.target.value)
                }
              />
              <input
                type="number"
                step="any"
                placeholder="Cintura (cm)"
                value={waist}
                onChange={(event: any) =>
                  setWaist(event.target.value)
                }
              />
              <input
                type="number"
                step="any"
                placeholder="Cadera (cm)"
                value={hip}
                onChange={(event: any) =>
                  setHip(event.target.value)
                }
              />
            </div>

            <div className="meta">
              Composición corporal
            </div>
            <div className="chart-form-row">
              <input
                type="number"
                step="any"
                placeholder="Grasa corporal (%)"
                value={bodyFat}
                onChange={(event: any) =>
                  setBodyFat(
                    event.target.value
                  )
                }
              />
              <input
                type="number"
                step="any"
                placeholder="Masa grasa (kg)"
                value={fatMass}
                onChange={(event: any) =>
                  setFatMass(
                    event.target.value
                  )
                }
              />
              <input
                type="number"
                step="any"
                placeholder="Masa magra (kg)"
                value={leanMass}
                onChange={(event: any) =>
                  setLeanMass(
                    event.target.value
                  )
                }
              />
              <input
                type="number"
                step="any"
                placeholder="Masa muscular (kg)"
                value={muscleMass}
                onChange={(event: any) =>
                  setMuscleMass(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="chart-form-row">
              <input
                type="number"
                step="any"
                placeholder="Agua corporal (%)"
                value={waterPct}
                onChange={(event: any) =>
                  setWaterPct(
                    event.target.value
                  )
                }
              />
              <input
                type="number"
                step="any"
                placeholder="Grasa visceral"
                value={visceralFat}
                onChange={(event: any) =>
                  setVisceralFat(
                    event.target.value
                  )
                }
              />
            </div>

            <div className="meta">
              Contexto y procedencia
            </div>
            <div className="chart-form-row">
              <select
                value={measurementContext}
                onChange={(event: any) =>
                  setMeasurementContext(
                    event.target.value
                  )
                }
              >
                <option value="BASELINE">
                  Línea basal
                </option>
                <option value="PRE_SESSION">
                  Antes de sesión
                </option>
                <option value="POST_SESSION">
                  Después de sesión
                </option>
                <option value="FOLLOW_UP">
                  Seguimiento
                </option>
              </select>

              <select
                value={sourceType}
                onChange={(event: any) =>
                  setSourceType(
                    event.target.value
                  )
                }
              >
                <option value="MANUAL">
                  Medición manual
                </option>
                <option value="BIOIMPEDANCE">
                  Bioimpedancia
                </option>
                <option value="BODY_SCAN">
                  Escáner corporal
                </option>
                <option value="OTHER">
                  Otro origen
                </option>
              </select>

              {programId && (
                <select
                  value={sessionId}
                  onChange={(event: any) =>
                    setSessionId(
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    Sin sesión específica
                  </option>
                  {sessions.map(
                    (session) => (
                      <option
                        key={session.id}
                        value={String(
                          session.id
                        )}
                      >
                        Sesión {
                          session.session_number
                        } · {
                          session.session_type
                        }
                      </option>
                    )
                  )}
                </select>
              )}
            </div>

            <input
              placeholder="Equipo o dispositivo utilizado"
              value={sourceDevice}
              onChange={(event: any) =>
                setSourceDevice(
                  event.target.value
                )
              }
            />

            <textarea
              rows={2}
              placeholder="Observaciones clínicas"
              value={notes}
              onChange={(event: any) =>
                setNotes(event.target.value)
              }
            />

            <div className="row">
              <button
                className="btn-primary"
                disabled={submitting}
                onClick={submit}
              >
                {submitting
                  ? "Guardando…"
                  : "Guardar medición"}
              </button>
            </div>
          </div>
        </div>
      )}
      {!latest && !open && <div className="chart-empty">Sin mediciones registradas.</div>}
      {latest && (
        <div className="chart-encounter-card">
          <span className="meta">
            Última medición ({formatDateTime(latest.measured_at)}): {latest.weight_kg ? `${latest.weight_kg} kg` : ""}
            {latest.bmi ? ` · IMC ${Number(latest.bmi).toFixed(1)}` : ""} — historial completo en la pestaña "Comparativas".
          </span>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Protocolos de tratamiento — plantillas (sólo lectura) + registros con
// firma real (ownership, mismo criterio que notas/recetas/órdenes).
// -----------------------------------------------------------------------
function ProtocolsSection({
  contactId,
  templates,
  records,
  professionals,
  defaultProfessionalId,
  myProfessionalId,
  onChanged,
}: {
  contactId: Id;
  templates: TreatmentProtocolTemplate[];
  records: TreatmentProtocolRecord[];
  professionals: Professional[];
  defaultProfessionalId: string;
  myProfessionalId: number | null;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [professionalId, setProfessionalId] = useState(defaultProfessionalId);
  const [summary, setSummary] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signingId, setSigningId] = useState<Id | null>(null);

  useEffect(() => setProfessionalId(defaultProfessionalId), [defaultProfessionalId]);

  async function submit() {
    if (!templateId) return show("Elegí una plantilla.", "error");
    if (!professionalId) return show("Elegí el profesional.", "error");
    setSubmitting(true);
    try {
      await api.createTreatmentProtocolRecord({
        contact_id: contactId,
        template_id: templateId,
        professional_id: professionalId,
        performed_at: new Date().toISOString(),
        data_json: { summary: summary || null },
      });
      show("Protocolo registrado en borrador.", "success");
      setSummary("");
      setOpen(false);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo registrar.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function sign(id: Id) {
    setSigningId(id);
    try {
      await api.signTreatmentProtocolRecord(id);
      show("Protocolo firmado.", "success");
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo firmar.", "error");
    } finally {
      setSigningId(null);
    }
  }

  return (
    <div>
      <SectionHeader title="Protocolos de tratamiento" open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <div className="chart-encounter-card">
          {templates.length === 0 ? (
            <p className="chart-empty">No hay plantillas de protocolo activas todavía — se administran fuera de esta pantalla.</p>
          ) : (
            <div className="chart-form">
              <div className="chart-form-row">
                <select value={templateId} onChange={(e: any) => setTemplateId(e.target.value)}>
                  <option value="">Plantilla...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
                  <option value="">Profesional...</option>
                  {professionals.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <textarea rows={2} placeholder="Resumen del protocolo realizado" value={summary} onChange={(e: any) => setSummary(e.target.value)} />
              <div className="row">
                <button className="btn-primary" disabled={submitting} onClick={submit}>
                  Guardar borrador
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {records.length === 0 && !open && <div className="chart-empty">Sin protocolos registrados.</div>}
      {records.map((r) => {
        const tpl = templates.find((t) => t.id === r.template_id);
        const summary = (r.data_json as any)?.summary;
        return (
          <div className="chart-encounter-card" key={r.id}>
            <div className="head">
              <span className={r.status === "SIGNED" ? "pill pill-ok" : "pill pill-warn"}>{r.status}</span>
              <span className="meta">{tpl?.name || `Plantilla #${r.template_id}`} · {formatDateTime(r.performed_at)}</span>
            </div>
            {summary && <p className="meta">{summary}</p>}
            {r.status === "DRAFT" && (
              <SignButton canSign={r.can_sign} signing={signingId === r.id} onSign={() => sign(r.id)} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------
// Implantes / dispositivos
// -----------------------------------------------------------------------
function ImplantsSection({ contactId, implants, onChanged }: { contactId: Id; implants: ImplantDeviceRecord[]; onChanged: () => void }) {
  const { show } = useToast();
  const [open, setOpen] = useState(false);
  const [itemType, setItemType] = useState("");
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!itemType.trim()) return show("Ingresá el tipo de implante/dispositivo.", "error");
    setSubmitting(true);
    try {
      await api.createImplantDevice({ contact_id: contactId, item_type: itemType, brand: brand || undefined, model: model || undefined, lot_number: lotNumber || undefined });
      show("Implante/dispositivo registrado.", "success");
      setItemType("");
      setBrand("");
      setModel("");
      setLotNumber("");
      setOpen(false);
      onChanged();
    } catch (e) {
      show(e instanceof ApiClientError ? e.message : "No se pudo registrar.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <SectionHeader title="Implantes y dispositivos" open={open} onToggle={() => setOpen((v) => !v)} />
      {open && (
        <div className="chart-encounter-card">
          <div className="chart-form">
            <div className="chart-form-row">
              <input placeholder="Tipo (ej. prótesis mamaria)" value={itemType} onChange={(e: any) => setItemType(e.target.value)} />
              <input placeholder="Marca" value={brand} onChange={(e: any) => setBrand(e.target.value)} />
            </div>
            <div className="chart-form-row">
              <input placeholder="Modelo" value={model} onChange={(e: any) => setModel(e.target.value)} />
              <input placeholder="Lote" value={lotNumber} onChange={(e: any) => setLotNumber(e.target.value)} />
            </div>
            <div className="row">
              <button className="btn-primary" disabled={submitting} onClick={submit}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
      {implants.length === 0 && !open && <div className="chart-empty">Sin implantes ni dispositivos registrados.</div>}
      {implants.map((i) => (
        <div className="chart-encounter-card" key={i.id}>
          <div className="head">
            <span className="pill">{i.item_type}</span>
            <span className="meta">
              {i.brand} {i.model} {i.lot_number ? `· Lote ${i.lot_number}` : ""}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
